import { ModelBillingHistory } from "@mcleod/ar/src/models/ModelBillingHistory";
import { CommonDialogs, ImageTable, ModelUsers } from "@mcleod/common";
import {
    BlurEvent, Button, ChangeEvent, Checkbox, CityState, ClickEvent, ComponentCreator, ContextMenuCreationEvent,
    CrudDecorator, DataDisplayEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, DataSourceValidationEvent, Dialog, DropdownSelectionEvent, EditRowDecorator, Image,
    Label, Layout, LayoutProvidedCrudDecoratorProps, Location, LookupModelSearchEvent, LookupModelSelectionEvent,
    MainPageAccessor, ModelDataChangeType, Panel, ParentSearchMode, SaveAction, SaveButton, ScreenStack,
    SlideoutDecorator, Snackbar, Switch, Table, TableAction, TableAddRowResult, TableContentsChangedEvent, TableRow,
    TableRowBeforeSaveEvent, TableRowDisplayEvent, TableRowMode, Tabset, Textbox, Toast, ValidationResult
} from "@mcleod/components";
import {
    ActionPermission, Api, ArrayUtil, CompanySettings, Currency, CurrencySettings, DisplayType, FieldUpdateEvent,
    HorizontalAlignment, LogManager, McLeodClassicIntegration, Model, ModelRow, ModelRowType, Navigation,
    PermissionsUtil, StringUtil, SuccessFail, UrlUtil, UserSettings, VerticalAlignment, CurrencyUtil
} from "@mcleod/core";
import { RowPostListener } from "@mcleod/core/src/ModelRow";
import { ModelEdiorderProfile, OrderLoadTenderHistory, RowEdiorderProfile } from "@mcleod/datafusion";
import { ModelEdiCustomer } from "@mcleod/datafusion/src/models/ModelEdiCustomer";
import {
    AssetUtil, getDispatchControlBoolean, LMECompanySettings, LMEGeneralSettings, LMEUserSettings, ResponsibilityUtil,
    UsersResponsibilitySlideout
} from "@mcleod/general";
import { ImageName, ImageNameUtil } from "@mcleod/images";
import { CarrierQuickInfo } from "@mcleod/powerbroker";
import { AppointmentUtil } from "./AppointmentUtil";
import { BatchOrderRouting } from "./asset/BatchOrderRouting";
import { AutogenLayoutCopyOrderPrompt } from "./autogen/AutogenLayoutCopyOrderPrompt";
import { AutogenLayoutOrders } from "./autogen/AutogenLayoutOrders";
import { BillOfLading } from "./BillOfLading";
import { BRLTLOrder, BrltlUtil } from "./brltl/BrltlUtil";
import { CarrierSelection } from "./CarrierSelection";
import { CarrierSelectionStepper } from "./CarrierSelectionStepper";
import { CheckDuplicateBOL, DuplicateBOLSource } from "./CheckDuplicateBOL";
import { CreateOrderLink } from "./CreateOrderLink";
import { DeliveryReceipt } from "./DeliveryReceipt";
import { DuplicateOrderPrompt } from "./DuplicateOrderPrompt";
import { ModelEquipmentGroup } from "./models/ModelEquipmentGroup";
import { ModelOrders } from "./models/ModelOrders";
import { MovementTabs } from "./MovementTabs";
import { MovementTabView } from "./MovementTabView";
import { OrderCreditValidator } from "./OrderCreditValidator";
import { OrderHandlingRequirementsSlideout } from "./OrderHandlingRequirementsSlideout";
import { OrderOverview } from "./OrderOverview";
import { OrderPostHist, OrderPostHistProps } from "./OrderPostHist";
import { OrderRates } from "./OrderRates";
import { OrdersEquipmentMatchListener } from "./OrdersEquipmentMatchListener";
import { OrderStop } from "./OrderStop";
import { OrderTabView } from "./OrderTabView";
import { PostToLoadBoards } from "./PostToLoadBoards";
import { RateUtil } from "./RateUtil";
import { RearrangeStopSlideout } from "./RearrangeStopSlideout";
import { RecurringOrderPrompt } from "./RecurringOrderPrompt";
import { ShipmentStatusSlideout } from "./ShipmentStatusSlideout";
import { StopProgress } from "./StopProgress";
import { TruckOrderNotUsed } from "./TruckOrderNotUsed";
import { VoidOrderPrompt } from "./VoidOrderPrompt";
import { getExciseTaxControlBoolean } from "../../ar/src/models/ModelExciseTaxControl";
import { UnvoidOrderPrompt } from "./UnvoidOrderPrompt";
import { ModelSalesperson } from "@mcleod/ar/src/models/ModelSalesperson";
import { OrderRerateLogic } from "./OrderRerateLogic";

const log = LogManager.getLogger("lme.dispatch.Orders");

const equipmentTypeOptionsMap = {
    tarps: "T",
    expedited: "E"
};
const ALL_ITEM = "ALL"

const DISABLE_TEXTBOX_DEF_MOVE_TYPE_STATUSES = ['P', 'D'];

const SEG_ALLOC_BASIS = ["A", "M", "Y", "Z"]

export function isSpotOrder(data: ModelRow | string): boolean {
    let status: string;
    if (data instanceof ModelRow) {
        status = data.get("subject_order_status");
    } else {
        status = data;
    }
    return new Set(["S", "C"]).has(status);
}

export function isReleasedToBilling(data: ModelRow): boolean {
    return data?.get("ready_to_bill") === true;
}

export enum NextAction {
    ASSIGN_CARRIER = "assignCarrier",
    ASSIGN_DRIVER = "assignDriver",
    FIND_CARRIER = "findCarrier",
    VIEW_ORDER = "viewOrder",
    NEW_ORDER = "newOrder"
}

const ORDER_USER_SHARED_FIELDS = ["operations_user", "agent_payee_id", "entered_user_id", "billing_user_id"];

const SEARCH_OVERRIDE_DISPLAY_TYPES = [
    DisplayType.DECIMAL, DisplayType.INTEGER, DisplayType.FLOAT, DisplayType.CURRENCY, DisplayType.DISTANCE,
    DisplayType.WEIGHT, DisplayType.PERCENTAGE, DisplayType.TEMPERATURE
];

interface BatchOrderEntry {
    equipmentGroup: string;
    items: ModelRow[];
}

export class Orders extends AutogenLayoutOrders {
    private _doAfterAction: () => void = () => this.mainDataSource.search({id: this.mainDataSource.activeRow.get("id")});
    private _nextAction: string | (() => void);
    private creditValidator: OrderCreditValidator;
    private checkDuplicateBOL: CheckDuplicateBOL;
    private _invokedFromEdi: boolean = false;
    private _currencyFromProfile: string = null;
    private loadingRecurringOrder: boolean = false;
    private _validateRespFilteringDuringAdd = (event: DataSourceValidationEvent) => this.validateRespFilteringDuringAdd(event, this.buttonUsers);
    private _enableNextActions: boolean;
    private _isBrokerageLtlCompany: boolean = false;
    private _isAsset: boolean = false;
    private postToLoadBoard: boolean = false;
    private carrierSelectionShouldRerate: boolean = false;
    private shouldOpenPaySlideout: boolean;
    private saveAction: SaveAction;
    private movementTabs: MovementTabs;
    private static orderCurrencies: string[] = ["freight_charge", "otherchargetotal", "total_charge",
        "excisetax_total", "totalcharge_and_excisetax", "est_tolls", "pay_gross", "order_value"];
    public carrierAssignMentChanged: () => void;
    private availableMovementId: string;
    private beforePostListener: RowPostListener;
    private static pwbLoadBoardServiceExist: boolean;
    private layoutBatchOrderRouting = Layout.getLayout("lme/dispatch/asset/BatchOrderRouting") as BatchOrderRouting;
    private _batchEntryMode: boolean = false;
    private currentMovementRow;
    private equipmentGroupRow;

    private batchOrderEntry: BatchOrderEntry = {
        equipmentGroup: "",
        items: []
    };

    public get batchEntryMode(): boolean {
        return this._batchEntryMode;
    }

    public set batchEntryMode(value: boolean) {
        this._batchEntryMode = value;
        this.howManyOrders.enabled = !value;
        this.buttonAssignDriver.enabled = !value;
    }

    override async onLoad(): Promise<void> {
        await this.initializePbwLoadBoardServiceExist();
        this.tabsetAddOrders["_header"].borderTopWidth = 0;
        this.movementTabs = this.layoutMovementTabs;
        this.isBrokerageLtlCompany = LMECompanySettings.getSingleton().isBrokerageLtl();
        if (this.isBrokerageLtlCompany) {
            this.setupBrltlDatasources();
        }
        this.creditValidator = new OrderCreditValidator(this);
        this.checkDuplicateBOL = new CheckDuplicateBOL(this);

        this.addOnSelectListeners();
        this.addRecurringOrderListener();
        this.addBeforeLookupModelListeners();

        this.stopsTable.onEditLayoutLoaded = this.stopTableOnEditLoaded();
        this.layoutRates.doAfterRating = () => this.creditValidator.validate();

        this.labelHoldBadge.tooltipDynamicContent = () => {
            return this.mainDataSource.activeRow?.get("hold_reason");
        }
        this.dataHeader["labelTitle"].minWidth = 220;

        this.checkboxTarps.addChangeListener(event => {
            if (event.userInitiatedChange) this.updateEquipTypeOptions();
        });
        this.checkboxExpedited.addChangeListener(event => {
            if (event.userInitiatedChange) this.updateEquipTypeOptions();
        });

        this.layoutRates.checkPickupAndDeliveryEntered = () => {
            const stopData = this.sourceStop.data;
            if (stopData.length < 2) return false;
            const pickupEntered = stopData[0].get("stop_type") === "PU" && stopData[0].get("city_id") != null;
            const deliveryEntered = stopData[stopData.length - 1].get("stop_type") === "SO" && stopData[stopData.length - 1].get("city_id") != null;
            return pickupEntered && deliveryEntered;
        }

        this.setDispatchSettings();
        this.addAdditionalMainDataSourceListeners();
        this.layoutRates.layoutMovementRates.panelOrderCharges.visible = false;
        this.setLoadboardFieldVisibility(this.switchPostToLoadBoards.checked);
        this.stopsTable.rowsAreDraggable = () => false;
        const partialShipmentsEnabled = getDispatchControlBoolean("enable_manifesting");
        this.textboxOrderLength.visible = partialShipmentsEnabled;
        this.sourceOrders.preventFocusFirstField = true;
        this.sourceOrders.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceOrdersAfterGetDataboundValues(row, dataSource);

        this.textboxOverrideArriveLate.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.validateArrivalWindow();
        };
        this.textboxOverrideArriveEarly.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.validateArrivalWindow();
        };
    }

    private async setOrderHistCommentsVisibility() {
        const hasAlerts = this.mainDataSource?.activeRow?.get("movement.has_load_lock_alert");
        if (StringUtil.equalsIgnoreCase(hasAlerts, 'Y')) {
            this.buttonOrderHistory.visible = false;
            this.buttonOrderHistoryWithAlerts.visible = true;
        } else {
            this.buttonOrderHistory.visible = true;
            this.buttonOrderHistoryWithAlerts.visible = false;
        }
    }

    async textboxOrderLengthOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const textboxOrderLength: Textbox = event.target as Textbox;
        const orderLength = textboxOrderLength.text;
        if (!StringUtil.isEmptyString(orderLength)) {
            const parsedValue = parseFloat(orderLength);
            if (isNaN(parsedValue) || parsedValue <= 0) {
                this.activeRow.set({order_length: null});
                await Snackbar.showWarningSnackbar('Order length must be greater than zero.');
                textboxOrderLength.focus();
            }
        }
    }

    async textboxPalletsHowManyOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const textboxPalletsHowMany: Textbox = event.target as Textbox;
        const palletLength = textboxPalletsHowMany.text;
        if (!StringUtil.isEmptyString(palletLength)) {
            const parsedValue = parseInt(palletLength);
            if (isNaN(parsedValue) || parsedValue <= 0) {
                this.activeRow.set({pallets_how_many: null});
                await Snackbar.showWarningSnackbar('Pallets must be greater than zero.');
                textboxPalletsHowMany.focus();
            }
        }
    }

    async setupMovementTabs(userInitiatedChange: boolean) {
        const orderRow = this.mainDataSource?.activeRow;
        const order_id = orderRow?.get("id");
        const loadboard = orderRow?.get("loadboard")
        if (StringUtil.isEmptyString(order_id)) {
            return;
        }

        let onlyAvailableMoves = null;

        const response = await Api.post("lme/dispatch/order-movements", {
            "order_id": order_id,
            "brokerage_only": false
        })
        if (response) {
            onlyAvailableMoves = this.filterAvailableMoves(response.data);
        }
        if (onlyAvailableMoves != null && onlyAvailableMoves.length > 1) {
            const movements = onlyAvailableMoves.map(move => new ModelRow(this.sourceMovement.url, false, move));
            if (this.movementTabs.currentTabId === undefined) {
                this.movementTabs.movements = undefined;
                if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("loadboard_post_movement_id"))) {
                    this.movementTabs.selectedMovementId = this.mainDataSource.activeRow.get("loadboard_post_movement_id", "");
                } else {
                    this.movementTabs.selectedMovementId = this.mainDataSource.activeRow.get("curr_movement_id", "");
                }
                this.movementTabs.movementTabOnClickCallback = (movement, newTab, oldTab) => this.movementTabOnClick(movement, newTab, oldTab);
                this.movementTabs.movementTabOnAdd = (movement) => this.movementTabOnSetup(movement);
                this.movementTabs.addMovementTabs(movements);
                const movementDates = this.movementTabs.panelMovementTabInfo.findComponentById(this.movementTabs.selectedMovementId + "-movement_pickup_dates") as Label;
                const movementCities = this.movementTabs.panelMovementTabInfo.findComponentById(this.movementTabs.selectedMovementId + "-panelTabInfo-labelFirstCityState") as Label;
                const movementTab = this.movementTabs.findComponentById(this.movementTabs.selectedMovementId) as Panel;
                movementTab.setProps({backgroundColor: "backgroundSubtle", zIndex: 10});
                movementDates.color = "subtle.darkest";
                movementCities.color = "subtle.darkest";
                movementCities.fontBold = true;
            }

            if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("loadboard_post_movement_id"))) {
                const loadboardPostMovementId = this.mainDataSource.activeRow.get("loadboard_post_movement_id", "");
                // Loop through movements to find the index for the movement that matches loadboard_post_movement_id
                for (let i = 0; i < movements.length; i++) {
                    if (movements[i].get("id") === loadboardPostMovementId) {
                        // Found the matching movement, perform actions on it
                        await this.setPostingCityAndOverrideFields(movements[i]);
                        if (userInitiatedChange) {
                            this.setLoadBoardDetailFields(movements[i]);
                        }
                        const movementDates = this.movementTabs.panelMovementTabInfo.findComponentById(loadboardPostMovementId + "-movement_pickup_dates") as Label;
                        const movementCities = this.movementTabs.panelMovementTabInfo.findComponentById(loadboardPostMovementId + "-panelTabInfo-labelFirstCityState") as Label;
                        const movementTab = this.movementTabs.findComponentById(loadboardPostMovementId) as Panel;
                        movementTab.setProps({backgroundColor: "backgroundSubtle", zIndex: 10});
                        movementDates.color = "subtle.darkest";
                        movementCities.color = "subtle.darkest";
                        movementCities.fontBold = true;
                        break;
                    }
                }
            } else if (movements.length > 0) {
                await this.setPostingCityAndOverrideFields(movements[0]);
                if (userInitiatedChange) {
                    this.setLoadBoardDetailFields(movements[0]);
                }
            }
        } else if (onlyAvailableMoves != null && onlyAvailableMoves.length > 0) {
            const movements = onlyAvailableMoves.map(move => new ModelRow(this.sourceMovement.url, false, move));
            await this.setPostingCityAndOverrideFields(movements[0]);
            if (userInitiatedChange) {
                this.setLoadBoardDetailFields(movements[0]);
            }
            this.availableMovementId = movements[0].get("id");
        }

    }

    filterAvailableMoves(data: any) {
        const filteredData = data.filter(row => row.status === "A");
        return filteredData.length > 0 ? filteredData : null;
    }

    private movementTabOnSetup(movement: ModelRow) {
        const movementTab = this.movementTabs.getMovementTab(movement);

        const statusImage = movementTab.tabInfo.findComponentById(`${movement?.get("id")}-panelTabInfo-${"imageStatus"}`) as Image;
        const textboxOrderNumber = movementTab.tabInfo.findComponentById(`${movement?.get("id")}-panelTabInfo-${"textboxOrderNumber"}`) as Textbox;
        statusImage.visible = false;
        textboxOrderNumber.visible = false;

        this.addMovementPickupTimes(movementTab.tabInfo, movement);

        const showMovementTopper = movement.get("brokerage") === "N" || !movement.isNull("split_image");
        if (showMovementTopper) {
            this.movementTabs.manageMovementToppers(movement.get("loaded") == "L", movementTab);
        }
        if (!movement.isNull("pending_payee_id") || !movement.isNull("pending_carrier_nbr")) {
            this.addPendingLabelToTab(movementTab.tabInfo, movement);
        }
        if (!movement.isNull("split_image")) {
            this.addSplitImageToTab(movementTab.tabInfo, movement);
        }
    }

    addMovementPickupTimes(tab: Panel, movement: ModelRow<any>) {
        const newPendingLabel = this.createPostedMovementTimeLabel(movement);
        tab.add(newPendingLabel);
    }

    addSplitImageToTab(tab: Panel, movement: ModelRow) {
        const splitImageName = ImageNameUtil.fromString(movement.get("split_image"));
        const imageSplit = new Image({
            name: splitImageName,
            color: splitImageName == ImageName.ysplitPick ? "success" : "primary",
            marginLeft: 8
        });
        tab.add(imageSplit);
    }

    async getPendingBadgeTooltipData(modelRow: ModelRow) {
        let tooltipData = null;
        if (modelRow?.isNull("pending_payee_id")) {
            const pendingCarrierType = modelRow.get("pending_carrier_type");
            const pendingCarrierNumber = modelRow.get("pending_carrier_nbr");
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow.get("id"),
                locked_user: modelRow.get("locked_user_id"),
                name: modelRow.get("pending_carrier_name"),
                pending_carrier: true,
                ...(pendingCarrierType == "M" ? {icc_number: pendingCarrierNumber} : {}),
                ...(pendingCarrierType == "D" ? {dot_number: pendingCarrierNumber} : {}),
                ...(pendingCarrierType == "I" ? {intrastate_code: pendingCarrierNumber} : {})
            });
        } else {
            const result = await Api.search("lme/powerbroker/carriers-for-assignment", {id: modelRow?.get("pending_payee_id")});
            const payeeData = result?.data[0];
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow?.get("id"),
                locked_user: modelRow?.get("locked_user_id"),
                name: payeeData.name,
                phone_number: payeeData.phone_number ?? "",
                icc_number: payeeData.icc_number,
                dot_number: payeeData.dot_number,
                intrastate_code: payeeData.intrastate_code,
                pending_carrier: false,
                id: payeeData.id
            });
        }
        return tooltipData;
    }

    async setTooltipPendingLabelCallback(pendingLabel: Label, modelRow: ModelRow) {
        const pendingTooltipData = await this.getPendingBadgeTooltipData(modelRow);
        if (pendingTooltipData) {
            pendingLabel.tooltipDynamicContent = async () => {
                return await this.getPendingBadgeLayout(pendingTooltipData);
            };
        }
    }

    private async getPendingBadgeLayout(modelRow: ModelRow): Promise<Layout> {
        const layout = await Layout.getLoadedLayout("lme/powerbroker/CarrierQuickInfo") as CarrierQuickInfo;
        layout.mainDataSource.data = [modelRow];
        layout.mainDataSource.rowIndex = 0;
        layout.pendingCancelled = (lock_changed, user_can_unlock) => {
            if (lock_changed && user_can_unlock) {
                this.mainDataSource.search({id: this.mainDataSource.activeRow.get("id")});
            }
        };
        return layout;
    }

    addPendingLabelToTab(tab: Panel, movement: ModelRow) {
        const newPendingLabel = this.createPendingLabel();
        this.setTooltipPendingLabelCallback(newPendingLabel, movement);
        tab.add(newPendingLabel);
    }

    createPendingLabel() {
        const pendingLabel = new Label({
            id: "pendingLabel", text: "Pending", backgroundColor: "warning", color: "warning.reverse",
            borderWidth: 1, borderRadius: 18, marginLeft: 10, marginRight: 10, fontSize: "small",
            paddingLeft: 20, paddingRight: 20, width: 110, fontBold: true, rowBreak: false
        });
        pendingLabel.align = HorizontalAlignment.CENTER;
        return pendingLabel;
    }

    createMovementTimeLabel(movement: ModelRow<any>) {
        const pendingLabel = new Label({
            id: "movement_pickup_dates",
            text: movement?.get("movement_sched_arrive_early"),
            fontSize: "medium",
            color: "subtle.dark",
            fontBold: true,
            width: 130,
            rowBreak: false
        });
        pendingLabel.align = HorizontalAlignment.CENTER;
        return pendingLabel;
    }

    createPostedMovementTimeLabel(movement: ModelRow<any>) {
        const pendingLabel = new Label({
            id: `${movement?.get("id")}-movement_pickup_dates`,
            text: movement?.get("movement_sched_arrive_early"),
            fontSize: "medium",
            color: "subtle.dark",
            fontBold: true,
            width: 130,
            rowBreak: false
        });
        pendingLabel.align = HorizontalAlignment.CENTER;
        return pendingLabel;
    }


    async movementTabOnClick(movement: ModelRow<any>, newTab: Panel, oldTab: Panel): Promise<void> {
        this.movementTabs.currentTabId = movement.get("id");
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("loadboard_post_movement_id")) && !StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow.get("loadboard_post_movement_id"), movement.get("id"))) {
            CommonDialogs.showYesNo("Changing the movement selection will update the current load posting with the information from the new movement. Do you want to continue?", "Update Posted Movement")
                .then(result => {
                    if (result === true) {
                        this.setPostingCityAndOverrideFields(movement);
                        this.textboxPnnRate.text = "";
                        this.updatePnnRateField(movement, false);
                        this.setTabText(newTab.id, "subtle.darkest");
                        this.setTabText(oldTab.id, "subtle.dark");

                    } else if (result === false || result === undefined) {
                        this.movementTabs.currentTabId = oldTab.id;
                        this.setTabText(oldTab.id, "subtle.darkest");
                        return;
                    }
                });
        } else {
            this.setTabText(newTab.id, "subtle.darkest");
            this.setTabText(oldTab.id, "subtle.dark");
            this.setPostingCityAndOverrideFields(movement);
            this.textboxPnnRate.text = "";
            this.updatePnnRateField(movement, true);
        }
    }

    setTabText(id: string, color: string) {
        const movementDates = this.movementTabs.panelMovementTabInfo.findComponentById(id + "-movement_pickup_dates") as Label;
        const movementCities = this.movementTabs.panelMovementTabInfo.findComponentById(id + "-panelTabInfo-labelFirstCityState") as Label;
        movementDates.color = color;
        movementCities.color = color;
        movementCities.fontBold = true;
    }

    updatePnnRateField(movement: ModelRow<any>, restoreExistingRate: boolean) {
        let pnnRate = movement.get("override_targetpay", movement.get("target_pay", null));
        if (restoreExistingRate) {
            if (!StringUtil.isEmptyString(this.mainDataSource.activeRow.get("pnn_rate"))) {
                pnnRate = this.mainDataSource.activeRow.get("pnn_rate");
            }
        }
        if (StringUtil.isEmptyString(this.mainDataSource.activeRow.get("pnn_rate")) || StringUtil.isEmptyString(this.textboxPnnRate.text)) {
            this.textboxPnnRateType.text = "Flat";
            this.mainDataSource.activeRow.set("pnn_rate_type", "F");
            this.textboxPnnRate.text = pnnRate;
        }
    }

    private async setEquipmentTypeFields() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const row = this.mainDataSource.activeRow;
        if (!StringUtil.isEmptyString(row.get("equipment_type_id"))) {
            await Model.searchSingleRecord("lme/dispatch/match-trailer-type", {id: row.get("equipment_type_id")},
                "lme/dispatch/EquipmentTypeAppliesToTrailer").then(equipRow => {
                    row.set("pnn_trailer_length", equipRow.get("equip_length"));
                    row.set("pnn_trailer_width", equipRow.get("equip_width"));
                })
        }

        // Set default values if not already set
        if (StringUtil.isEmptyString(row.get("pnn_trailer_length"))) {
            row.set("pnn_trailer_length", 53);
        }

        this.mainDataSource.displayDataInBoundComponents();
    }

    async setLoadBoardDetailFields(movement: ModelRow<any>) {
        await this.setEquipmentTypeFields();
        await PostToLoadBoards.setUserInfoFields(
            this.mainDataSource.activeRow,
            this.textboxPnnLoadboardUserId,
            this.textboxPnnLoadboardUserIdOnLookupModelSelection
        );
        this.updatePnnRateField(movement, false);
    }

    async setPostingCityAndOverrideFields(movement: ModelRow) {
        await this.setPostingCityState(movement?.get("first_city_id"));
        if (StringUtil.isEmptyString(this.textboxOverrideArriveEarly.text)) {
            this.textboxOverrideArriveEarly.text = movement?.get("movement_sched_arrive_early");
        }
        if (!StringUtil.isEmptyString(movement?.get("movement_sched_arrive_late"))) {
            if (StringUtil.isEmptyString(this.textboxOverrideArriveLate.text)) {
                this.textboxOverrideArriveLate.text = movement?.get("movement_sched_arrive_late");
            }
            this.textboxOverrideArriveLate.visible = this.switchPostToLoadBoards.checked;
            this.andLabel.visible = this.switchPostToLoadBoards.checked;
        } else {
            this.textboxOverrideArriveLate.visible = false;
            this.andLabel.visible = false;
        }
    }

    private textboxPnnLoadboardUserIdOnLookupModelSelection = (event: LookupModelSelectionEvent) => {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.hasSelection() === true && this?.mainDataSource?.activeRow) {
            this.mainDataSource.activeRow.set("pnn_callback", event.selection.get("phone"));
            this.textboxPnnCallback.displayData(this.mainDataSource.activeRow, null, 0);
            this.textboxPnnCallback.validate(false, false);
        }
    }

    textboxPnnLoadboardUserIdOnChange(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const orderRow = this.mainDataSource.activeRow;
        if (orderRow != null) {
            this.setUpdatedUserPnnCallbackNumber(orderRow.get("pnn_loadboard_user_id"));
        }
    }

    private async setUpdatedUserPnnCallbackNumber(userId: string) {
        if (userId != null) {
            await new ModelUsers().searchSingle({id: userId}, "common/UserLookup").then(userRow => {
                if (!userRow) return;
                if (userRow.get("phone")) {
                    this.mainDataSource.activeRow.set("pnn_callback", userRow.get("phone"));
                    this.textboxPnnCallback.text = userRow.get("phone");
                } else {
                    this.mainDataSource.activeRow.set("pnn_callback", "");
                    this.textboxPnnCallback.text = "";
                }
            })
        }
    }


    private _setPreferredVendorLookupModelData(row: ModelRow) {
        const lookupData = row?.get("preferred_vendors_lookup_data");
        if (lookupData != null) {
            this.switchPostToAllServices.checked = StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow.get("all_vendors_enabled"), "Y");
            this.switchPostToAllServices.visible = this.switchPostToLoadBoards.checked;
            this.textboxPreferredVendors.visible = this.switchPostToAllServices.visible && !this.switchPostToAllServices.checked;
            this.textboxPreferredVendors.visibleDuringUpdate = true;
            const newLookupData = lookupData.map(data => {
                const row = new ModelRow("lme/powerbroker/pbw-loadboard-services", false, {...data});
                row.type = ModelRowType.LOOKUP_MODEL_DATA;
                return row;
            });
            row.setLookupModelData("preferred_vendors", newLookupData);
            row.set("preferred_vendors_lookup_data", undefined);
        }
    }

    private async setCommodity(apiData?: any) {
        const commodityId = this.mainDataSource.activeRow.get("commodity_id");
        if (commodityId === null || commodityId === undefined) return;
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", {id: commodityId});

        if (response != null) {
            const lmModelRow = new ModelRow(this.textboxCommodityId.lookupModel, false, {
                id: response.get("id"),
                descr: apiData?.commodity ?? response.get("descr"),
                temperature_min: apiData?.temperature_min ?? response.get("temperature_min"),
                temperature_max: apiData?.temperature_max ?? response.get("temperature_max"),
                is_hazmat: response.get("is_hazmat"),
                hazmat_code: response.get("hazmat_number"),
                hazmat_number: response.get("hazmat_number"),
                hazmat: response.get("hazmat")
            });

            lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
            this.textboxCommodityId.dataSource.activeRow.setLookupModelData("commodity_id", lmModelRow);
            this.textboxCommodityId.dataSource.activeRow.set("commodity_id", response.get("id"));
            this.textboxCommodityId.displayData(this.textboxCommodityId.dataSource.activeRow, null, 0);

            this.textboxHazmatCode.dataSource.activeRow.setLookupModelData("hazmat_code", lmModelRow);
            this.textboxHazmatCode.dataSource.activeRow.set("hazmat_code", response.get("hazmat_number"));
            this.textboxHazmatCode.displayData(this.textboxHazmatCode.dataSource.activeRow, null, 0);

        }

        this.commodityCodeChanged();
    }

    private addAdditionalMainDataSourceListeners() {
        this.mainDataSource.componentValidationFailedCallback = this.onComponentValidationFailed;
        this.mainDataSource.afterGetDataboundValues = this.sourceOrdersAfterGetDataboundValues;
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            // Do additional validation here before posting
            let result = true;
            if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
                result = await new OrdersEquipmentMatchListener(this).rowUpdated();
            }
            if (this.switchPostToLoadBoardsNextAction.checked == true) {
                const brltlData: BRLTLOrder = {
                    revenue_code_id: this.activeRow.get("revenue_code_id"),
                    order_type_id: this.activeRow.get("order_type_id"),
                    order_mode: this.activeRow.get("order_mode"),
                    no_freight_group_items: this.sourceBrltlOrderFreightGroupItem.data.length == 0
                }
                if (BrltlUtil.isBRLTLOrder(brltlData)) {
                    this.switchPostToLoadBoardsNextAction.checked = false;
                    Snackbar.showWarningSnackbar("Brokerage LTL orders may not be posted to the load boards.");
                    result = false;
                }
            }
            return result
        }
        this.mainDataSource.addAfterSearchPlusChildrenListener(() => {
            new OrdersEquipmentMatchListener(this).rowUpdated(false);
        })
    }

    buttonAutoEquipmentMatchOnClick(event: ClickEvent) {
        this.triggerAutoMatch();
    }

    async triggerAutoMatch() {
        await new OrdersEquipmentMatchListener(this).rowUpdated();
        this.layoutHandlingReqs.populateHdrs(this.sourceBrltlOrderHdrXFgp.data, true, true).then(() => {
            this.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
        });
        this.sourceEquipMatchDetail.displayDataInBoundComponents();
    }

    private addOnSelectListeners() {
        this.textboxEquipmentTypeIdGeneral.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            if (event.hasSelection() === true) {
                this.textboxEquipmentTypeIdEquipment.text = this.textboxEquipmentTypeIdGeneral.text;
                if (this.mpactConfigured())
                    this.layoutRates.calculateRates(false, false);
            }
        });

        this.textboxEquipmentTypeIdEquipment.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            if (event.hasSelection() === true) {
                this.textboxEquipmentTypeIdGeneral.text = this.textboxEquipmentTypeIdEquipment.text;
                if (this.mpactConfigured())
                    this.layoutRates.calculateRates(false, false);
            }
        });

        this.textboxRevenueCodeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            if (event.hasSelection() === true) {
                if (this.textboxRevenueCodeId.getFirstLookupModelData()?.get("id") != null)
                    this.revenueCodeChanged(this.textboxRevenueCodeId.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxOrderType.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            if (event.hasSelection() === true) {
                if (this.textboxOrderType.getFirstLookupModelData()?.get("id") != null)
                    this.orderTypeChanged(this.textboxOrderType.getFirstLookupModelData().get("id"));
            }
        });

        this.textboxCustomerId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            if (event.hasSelection() === true) {
                if (this.textboxCustomerId.getFirstLookupModelData()?.get("id") != null)
                    (this.layoutOverview as OrderOverview).customerChanged(this.textboxCustomerId.getFirstLookupModelData());
                this.creditValidator.customerId = this.textboxCustomerId.getFirstLookupModelData()?.get("id");

                if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
                    const acctManager = event.selection.get("account_mgr_user_id", "");
                    if (acctManager) {
                        const userRow = await new ModelUsers().searchSingle({id: acctManager}, "common/UserLookup");
                        if (userRow) {
                            this.mainDataSource.activeRow.setValueAndLookupModelData("operations_user", acctManager, userRow);
                        }
                    }
                    await this.defaultSalespersonFromCustomer(event.selection);
                }

                if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    const currencyCode = event.selection.get("currency_type");
                    const freightCharge = this.activeRow.getCurrency("freight_charge");
                    if (freightCharge && currencyCode) {
                        freightCharge.setCurrencyCode(currencyCode);
                        this.activeRow.updateCurrency("freight_charge", freightCharge);
                    }
                    await this.layoutRates.convertPricingCurrencies(event.selection.get("currency_type", await this.getTransactionalCurrency()));
                    if ((this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE)
                        && !this.mainDataSource.activeRow.isNull("customer_id")) {
                        const isExciseTaxable = event.selection.get("excise_taxable") != null ? event.selection.getBoolean("excise_taxable", false) : getExciseTaxControlBoolean("create_orders_taxable");
                        this.layoutRates.switchTaxableOrder.checked = isExciseTaxable;
                        this.layoutRates.panelExciseTaxTotal.visible = isExciseTaxable;
                    }
                }
            }
        });

        this.textboxDefMoveType.addChangeListener((event: ChangeEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            this.isAsset = event.newValue === "Asset";
            if (!this.textboxDefMoveType.isDropdownVisible() && event.userInitiatedChange) {
                this.tabLoadBoardDetails.visible = (!this.isAsset && this.mainDataSource.mode !== DataSourceMode.ADD && !PermissionsUtil.isUserDeniedLayout("lme/dispatch/PostToLoadBoards"));
                this.enableNextActions = true;
            }
        });
    }

    private addBeforeLookupModelListeners() {
        const activeFilter = event => event.filter.active = "Y";
        const userfilter = event => event.filter.web_user_type = "U";
        this.textboxBillingUserId.addBeforeLookupModelSearchListener(userfilter);
        this.textboxEnteredUserId.addBeforeLookupModelSearchListener(userfilter);
        this.textboxAccountManager.addBeforeLookupModelSearchListener(userfilter);
        this.textboxBrkQualProfile.addBeforeLookupModelSearchListener(activeFilter);
        this.textboxBrokerageWorkflowId.addBeforeLookupModelSearchListener(activeFilter);
        this.textboxOrderOrderedBy.addBeforeLookupModelSearchListener(event => {
            event.filter.parent_row_id = this.mainDataSource.activeRow?.get("customer_id");
            event.filter.parent_row_type = "C";
        });
        this.textboxPreferredVendors.addBeforeLookupModelSearchListener(event => {
            event.filter.is_active = "Y";
        });

        this.textboxOrderOrderedBy.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true)
                this.mainDataSource.activeRow.set("ordered_by_id", event.selection.get("id"))
        });

        this.textboxPreloadTrailerId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.is_active = "A";
            const row = this.sourceStop.data[0];
            if (!row.isEmpty() && !row.isNull("location_id")) {
                event.filter.location_id = row.get("location_id");
            }
        })
    }

    private mpactConfigured(): boolean {
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        return dispatchControl.target_pay_calc == "M" &&
            !StringUtil.isEmptyString(dispatchControl.mpact_smart_api_key) &&
            !StringUtil.isEmptyString(dispatchControl.mpact_host_url);
    }

    public get isAsset(): boolean {
        return this._isAsset;
    }

    public set isAsset(value: boolean) {
        this._isAsset = value;
        this.buttonAssignDriver.visible = value;
        this.buttonFindCarrier.visible = !value;
        this.buttonAssignCarrier.visible = !value && !this.labelCreditBadge.visible;
        this.switchPostToLoadBoardsNextAction.visible = !value;
        this.textboxBrkQualProfile.visible = !value;
        this.textboxBrokerageWorkflowId.visible = !value;
        this.tabLoadBoardDetails.visible = !value && this.mainDataSource.mode !== DataSourceMode.ADD && !PermissionsUtil.isUserDeniedLayout("lme/dispatch/PostToLoadBoards");
        this.tabNextActions.visible = !(value && this.displayingFromToolbox) && this.mainDataSource.mode === DataSourceMode.ADD;
        this.layoutRates.carrierPayLayout.visible = !AssetUtil.isAssetMoveType(value)
            && (this.activeRow == null || !this.activeRow.getBoolean("multi_movement", false))
        if (this.mainDataSource.mode === DataSourceMode.ADD && !AssetUtil.isAssetMoveType(value) && !this.loadingRecurringOrder && !this.getInvokedFromEdi()) {
            this.layoutRates.calculateRates(false, false);
        }
        this.layoutRates.tabsetSuggestedPricing.visible = !AssetUtil.isAssetMoveType(value);
        if (value && this.tableEquipmentRequirementsBrokerage.visible) {
            this.tableEquipmentRequirementsBrokerage.visible = false;
            this.tableEquipmentRequirementsAsset.visible = true;
            this.tableEquipmentRequirementsAsset.dataSource.displayDataInBoundComponents();
        } else if (!value && this.tableEquipmentRequirementsAsset.visible) {
            this.tableEquipmentRequirementsAsset.visible = false;
            this.tableEquipmentRequirementsBrokerage.visible = true;
            this.tableEquipmentRequirementsBrokerage.dataSource.displayDataInBoundComponents();
        }
    }

    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }

    public set isBrokerageLtlCompany(value: boolean) {
        this._isBrokerageLtlCompany = value;
        this.tabBrltlFreightItems.visible = value;
        this.tabAccessorials.visible = value;
        this.layoutRates.panelRatingDetails.visible = value;
        if (value && this.mainDataSource.mode === DataSourceMode.ADD) {
            this.addBlankItemIfBRLTL();
            this.textboxOrderMode.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
            this.textboxRevenueCodeId.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
            this.textboxOrderType.addBlurListener(() => {
                this.addBlankItemIfBRLTL();
            });
        }

        if (value) {
            this.layoutFreightItems.doBeforeCommoditySearch = (filter: any) => {
                filter.product_book_location = this.sourceStop?.data?.[0].get("location_id");
                filter.product_book_customer = this.activeRow.get("customer_id");
            };
            this.textboxBolNote.visible = true;
        }
    }

    set enableNextActions(value: boolean) {
        this._enableNextActions = value;
        if (this.getInvokedFromEdi()) return;
        if (this._enableNextActions && !this.displayingFromToolbox) {
            const userSettings = LMEUserSettings.get();

            let isAsset: boolean;
            if (this.textboxDefMoveType?.selectedItem?.value) {
                isAsset = this.textboxDefMoveType?.selectedItem?.value === "A";
            } else {
                isAsset = (AssetUtil.isAssetMoveType(this.sourceOrders))
            }

            const orderNextActionDefault = (isAsset)
                ? userSettings.asset_new_order_next_action
                : userSettings.new_order_next_action;
            switch (orderNextActionDefault) {
                case "N": {
                    this.nextAction = NextAction.NEW_ORDER;
                    this.buttonEnterNewOrder.clicked();
                    break;
                }
                case "A": {
                    this.nextAction = NextAction.ASSIGN_CARRIER;
                    this.buttonAssignCarrier.clicked();
                    break;
                }
                case "D": {
                    this.nextAction = NextAction.ASSIGN_DRIVER;
                    this.buttonAssignDriver.clicked();
                    break;
                }
                case "F": {
                    this.nextAction = NextAction.FIND_CARRIER;
                    this.buttonFindCarrier.clicked();
                    break;
                }
                case "V":
                default: {
                    this.nextAction = NextAction.VIEW_ORDER;
                    this.buttonViewOrder.clicked();
                    break;
                }
            }
        } else {
            this.panelHowManyOrders.visible = false;
            this.panelNextActionButtons.visible = false;
            this.panelNextActions.padding = 12;
        }
    }

    private onComponentValidationFailed(result: ValidationResult): void {
        const comp = result.component;
        if (comp?.owner instanceof OrderStop)
            comp.owner.onComponentValidationFailed(result);
    }

    private updateEquipTypeOptions() {
        const options = [];
        if (this.checkboxTarps.checked) {
            options.push(equipmentTypeOptionsMap.tarps);
        }
        if (this.checkboxExpedited.checked) {
            options.push(equipmentTypeOptionsMap.expedited);
        }
        if (options.length > 0) this.activeRow?.set("equipment_type_options", options.join("|"));
    }

    async dataheaderOnContextMenuCreation(event: ContextMenuCreationEvent) {
        // Capture the current active row to avoid null reference issues in click handlers
        const currentActiveRow = this.activeRow;

        if (currentActiveRow == null || this.mainDataSource.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }
        const orderMovements = await this.getOrderMovements();
        const currentMovement = this.getCurrentMovementRow(orderMovements);
        const equipmentGroupRow = await this.getEquipmentGroupRow(currentMovement);
        const isMovementCovered = await this.isMovementCovered(currentMovement, equipmentGroupRow);

        const copyControlRecord = await Model.search("lme/dispatch/copy-control-lookup");
        const copyToOtherCompanyDisabled = copyControlRecord.actualRowCount == 0;

        const copyOtherCompanies = await Model.search("common/company-lookup");

        event.contextMenu.updateMenuLabels({
            convertToOrder: {
                visible: isSpotOrder(currentActiveRow) && currentActiveRow.get("status") !== "V",
                onClick: (event) => this.convertSpotToOrder(event)
            },
            duplicateOrder: {
                visible: !isSpotOrder(currentActiveRow),
                onClick: (event) => this._duplicateOrder()
            },
            getTerminalInfo: {
                visible: currentActiveRow.getBoolean("is_brokerage_ltl", false),
                onClick: (event) => BrltlUtil.getTerminalInfo(currentActiveRow.get("id"))
            },
            createRecurringOrder: {
                onClick: (event) => this.createRecurringOrder(event)
            },
            voidOrder: () => {
                const isUpdateOrdersAfterBilledDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER);
                const isBilled = !currentActiveRow.isNull("bill_date");
                const isTransferred = !currentActiveRow.isNull("xferred2billing_dt");
                const isAvailable = currentActiveRow.get("status") === "A";
                return {
                    visible: isAvailable && !((isBilled || isTransferred) && isUpdateOrdersAfterBilledDenied),
                    enabled: !isMovementCovered,
                    disabledTooltip:isMovementCovered === "D"
                        ? "Cannot void movement with assigned equipment"
                        : isMovementCovered === "C"
                        ? "Cannot void movement with assigned carrier"
                        : "",
                    onClick: (event) => this.voidOrder(event)
                };
            },
            createRate: {
                visible: ["A", "P", "D", "V"].includes(currentActiveRow.get("status")),
                onClick: (event) => this.createRate(event)
            },
            copyOrder: () => {
                const isBilled = !currentActiveRow.isNull("bill_date");
                const isTransferred = !currentActiveRow.isNull("xferred2billing_dt");
                const isSubjectOrder = !currentActiveRow.isNull("subject_order_status");
                const hasBeenCopied = !currentActiveRow.isNull("copy_to_order_id");

                // Determine the reason for being disabled
                let disabledReason = "";
                if (copyToOtherCompanyDisabled) {
                    disabledReason = "Copy to other company is disabled in duplicate control settings";
                } else if (isSubjectOrder) {
                    disabledReason = "Cannot copy subject orders";
                } else if (isTransferred) {
                    disabledReason = "Cannot copy order that has been transferred to billing";
                } else if (isBilled) {
                    disabledReason = "Cannot copy order that has been billed";
                } else if (hasBeenCopied) {
                    disabledReason = "This order has already been copied to company [" + currentActiveRow.get("copy_to_company") + "] as Order #[" + currentActiveRow.get("copy_to_order_id") + "]";
                }

                return {
                    enabled: (!((isBilled || isTransferred))) && !isSubjectOrder && !copyToOtherCompanyDisabled && !hasBeenCopied,
                    visible: copyOtherCompanies.actualRowCount > 1,
                    disabledTooltip: disabledReason,
                    onClick: (event) => this._copyOrder(event)
                }
            },
            truckOrderNotUsed: () => {
                const orderId = currentActiveRow.get("id");
                const isAvailable = currentActiveRow.get("status") === "A";
                const isAsset = currentActiveRow.getString("def_move_type") === "A";
                const isManifested = this.isManifestedOrder(orderMovements);
                const isTruckload = currentActiveRow.getString("order_mode") === "T";
                const hasSplit = this.hasSplitPoint();
                const driver = equipmentGroupRow?.get("driver_id");
                let disabledReason = "";
                if (!isAvailable) {
                    disabledReason = "TONU can only be processed for available orders.";
                }
                else if (!isAsset) {
                    disabledReason = "TONU cannot be processed for brokerage orders.";
                }
                else if (isManifested) {
                    disabledReason = "Order must be dropped from manifest before proceeding with TONU.";
                }
                else if (!isTruckload) {
                    disabledReason = "TONU cannot be processed for Partial or LTL orders.";
                }
                else if (hasSplit) {
                    disabledReason = "TONU is not allowed for Y-Splits.  Please remove the Y-Split before proceeding.";
                }
                return {
                    enabled: isAvailable && isAsset && !isManifested && isTruckload && !hasSplit,
                    visible: isAvailable,
                    disabledTooltip: disabledReason,
                    onClick: (event) => TruckOrderNotUsed.openSlideout({
                        orderId,
                        driverId: driver,
                        doAfterSave: () => {
                            OrderTabView.navigateTo({key: currentActiveRow.get("id"), newTab: false,
                                doAfterNavigate: () => TruckOrderNotUsed.showSuccessToast(orderId, false)
                            });
                        }
                    }),
                }
            },
            unvoidOrder: () => {
                return {
                    visible: currentActiveRow.get("status") === "V",
                    onClick: (event) => this._unvoidOrder(event)
                };
            }
        });
    }

    private isManifestedOrder(movements: ModelRow[]): boolean {
        let hasManifest = false;
        if (movements == null) {
            return false;
        }
        for(const movement of movements) {
            if (!movement.isNull("manifest_id")) {
                hasManifest = true;
                break;
            }
        }
        return hasManifest;
    }

    async isMovementCovered(currentMovement: any, equipmentGroupRow: ModelRow) {
        if (currentMovement == null) return false;
        try {
            const isAsset = AssetUtil.isAssetMoveType(this.sourceOrders);
            if (isAsset) {
                if (!equipmentGroupRow) {
                    return false;
                }
                if (
                    !StringUtil.isEmptyString(equipmentGroupRow.get("tractor_id")) ||
                    !StringUtil.isEmptyString(equipmentGroupRow.get("driver_id")) ||
                    !StringUtil.isEmptyString(equipmentGroupRow.get("driver2_id"))
                ) {
                    return "D";
                }
            }
            else {
                const carrierId = currentMovement instanceof ModelRow ? currentMovement.get("override_payee_id") : currentMovement.override_payee_id;
                if (carrierId !== null && !StringUtil.isEmptyString(carrierId)) {
                    return "C";
                }
            }
            return false;

        } catch (error) {
            log.error("Error checking movement coverage:", error);
            return false;
        }
    }

    private async getEquipmentGroupRow(currentMovement: ModelRow): Promise<ModelRow>  {
        try {
            if (!currentMovement) {
                return undefined;
            }

            const equipGroupid = currentMovement.get("equipment_group_id");
            return await new ModelEquipmentGroup().searchSingle({ id: equipGroupid });
        } catch (error) {
            log.error("Error getting equipment group:", error);
            return undefined;
        }
    }

    private async getOrderMovements(): Promise<ModelRow[]> {
        const order_id = this.activeRow?.get("id");
        if (!order_id) {
            return undefined;
        }

        try {
            return await Api.post("lme/dispatch/order-movements", {
                "order_id": order_id,
                "brokerage_only": false
            }).then((response) =>
                response.data.map((movementData: any) =>
                    new ModelRow("lme/dispatch/OrderMovement", false, movementData))
            );
        } catch (error) {
            log.error("Error getting current movement row:", error);
            return undefined;
        }
    }

    private getCurrentMovementRow(modelRows: ModelRow[]): ModelRow {
        const curr_movement_id = this.activeRow?.get("curr_movement_id");
        if (!curr_movement_id) {
            return undefined;
        }
        return modelRows.find(movement => movement.get("id") === curr_movement_id);
    }

    // Used in onClickListener for Manage Stops button
    private _manageStops() {
        if (this.sourceStop.hasChanged()) {
            Snackbar.showWarningSnackbar("Save previous stop edits before opening Manage Stops.");
            return;
        }
        const slideout = new RearrangeStopSlideout("O", this.mainDataSource?.activeRow.get("id"));
        slideout.showSlideout(
            (manageLayout: ManageStops) => {
            },
            (canceled: boolean) => {
                if (!canceled) {
                    Toast.showSuccessToast("Changes were saved successfully.");
                    OrderTabView.navigateTo({key: this.activeRow.get("id"), newTab: false});
                }

            }
        );
    }

    async textboxCustomerIdOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused && this.mainDataSource.activeRow.isNull("customer_id"))
            this.creditValidator.customerId = null;

        if (this.mainDataSource.mode == DataSourceMode.ADD && !this.mainDataSource.activeRow.isNull("customer_id") && !this.mainDataSource.activeRow?.isNull("blnum") && !this.switchRecurringOrder.checked) {
            this.checkForDuplicateBOL(DuplicateBOLSource.ADD)
                .then((response) => {
                    if (!response.success)
                        this.textboxBlnum.validationWarning = response.reason;
                });
        }

        if ((this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) && !this.mainDataSource.activeRow.isNull("customer_id")) {
            const ediCustomerRowCount = (await new ModelEdiCustomer().search({"customer_id": this.mainDataSource.activeRow?.get("customer_id")})).actualRowCount;

            this.stopsTable.rows?.forEach(tableRow => {
                const orderStop = tableRow["orderStop"] as OrderStop;
                orderStop.textboxLocationId.googlePlacesProps.customerId = this.mainDataSource.activeRow?.get("customer_id");

                if (this.sourceOrders.activeRow?.get("ordered_method") === "M") {
                    if (ediCustomerRowCount === 0) {
                        orderStop.textboxEdiLocCode.visible = false;
                        orderStop.dfXref.visible = false;
                    } else {
                        orderStop.textboxEdiLocCode.visible = true;
                        if (orderStop.textboxEdiLocCode.text !== "") {
                            orderStop.dfXref.visible = true;
                        }
                    }
                }
            });
        }
    }

    async checkStopsForDistanceChange(checkDistance: boolean = true) {
        if (!checkDistance) return;
        const stopdata = this.sourceStop.data;
        let changedText = this.sourceStop.deletedData?.length > 0 ? "removed from" : null;
        for (let i = 0; changedText == null && i < stopdata?.length; i++) {
            if (stopdata[i]._appending) changedText = "added to";
            const changedData = stopdata[i].getChangedData();
            if (changedData?.city_id != null || changedData?.address != null || changedData?.stop_type) {
                changedText = "modified on";
            }
        }
        const locked = this.activeRow?.getBoolean("lock_miles", false)
        if (changedText != null) {
            if (locked) {
                const message = `A stop has been ${changedText} this order which may affect mileage.  Would you like to unlock the mileage?`;
                const unlock = await CommonDialogs.showYesNo(message, "Unlock Miles");
                if (unlock) {
                    this.activeRow.set("lock_miles", "N");
                    this.activeRow.set("bill_distance", null);
                }
            } else {
                this.activeRow?.set("bill_distance", null);
            }
        } else {
            this.activeRow?.set("bill_distance", this.activeRow?.originalData["bill_distance"]);
        }
    }

    enableOnHoldComponents(value: boolean) {
        this.switchHoldLoad.tooltipDynamicContent = () => {
            if (!value) {
                return new Panel({
                    width: 225,
                    padding: 0,
                    components: [
                        new Label({ text: "Order Cannot Be Taken Off Hold", rowBreak: true, fontBold: true }),
                        new Label({ text: "The Pending Credit Override must be approved to take order off hold" })
                    ]
                });
            }
        }
        this.switchHoldLoad.enabled = value;
        this.textboxHoldReason.enabled = value;
    }

    private addRecurringOrderListener() {
        this.textboxRecurringOrderId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            const selectedRow = event.selection;
            if (this.sourceOrders.mode == DataSourceMode.ADD) {
                if (selectedRow != null) {
                    await this.mainDataSource.setComponentsBusy(true);
                    const loadingSnackbar = Snackbar.showDownloadSnackbar("Recurring Order", "Loading...", { targetPanel: this });
                    try {
                        const response = await Api.search("lme/dispatch/use-recurring-order", {
                            "recurring_order_id": selectedRow.get("id"),
                            "batch_entry_mode": this.batchEntryMode
                        });
                        this.loadingRecurringOrder = true;
                        await this.loadFromApiData(response.data[0], DataSourceMode.ADD);
                        const recurringCustomerData = this.textboxCustomerId.getFirstLookupModelData();
                        const recurringCustomerSalesperson = recurringCustomerData?.get("salesperson_id", "")
                            || this.mainDataSource.activeRow.get("salesperson_id", "");

                        await this.setSalespersonValue(recurringCustomerSalesperson);
                        this.stopsTable.dataSource.displayDataInBoundComponents();
                        this.addBlankItemIfBRLTL();
                        this.enableNextActions = true;
                        this.switchBatchOrder.enabled = !this.hasSplitPoint();
                    } finally {
                        this.mainDataSource.setComponentsBusy(false);
                        await loadingSnackbar.dismiss();
                    }
                } else if (this.panelBatchOrder.visible) {
                    this.switchBatchOrder.enabled = true;
                }
                this.switchBatchOrder.tooltip = this.switchBatchOrder.enabled ? "Enter equipment assignment and deliver order" :
                    "Cannot use batch entry with a recurring order that has split points";
            }
        });
    }

    private addBlankItemIfBRLTL() {
        if (!this.tabBrltlFreightItems.visible) return;
        const brltlData = this.getBRLTLData()
        if (BrltlUtil.shouldRateUsingTariff(brltlData)) {
            if (this.layoutFreightItems.tableFreightItems.rows.length == 0) {
                this.layoutFreightItems.addFreightItemToTable();
            }
        } else {
            this.layoutFreightItems.removeBlankFreightItem();
        }
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return {customDataLoader: this.loadFromApiData};
    }

    public loadFromApiData(apiData: any, mode: DataSourceMode) {
        const ratesLayout = this.layoutRates as OrderRates;
        this.removeChildDataSources();
        const order = new ModelRow("lme/dispatch/orders", true, apiData);
        this.checkRateExpiration(apiData.is_rate_expired);
        //set the stop data first so that when we set the order dataSource mode, we can not load the default 2 stops
        this.loadChildTableData(this.stopsTable, this._getArrayAndNull(order, "stops"), mode);
        this.loadChildTableData(ratesLayout.tableOtherCharges, this._getArrayAndNull(order, "other_charges"), mode);
        this.loadChildTableData(this.layoutFreightItems.tableFreightItems, this._getArrayAndNull(order, "freight_group_items"), mode);
        this.getDFHandlingRequirementData(order);
        this.mainDataSource.setRowsAndMode(mode, [order], () => this._displayDataCallback(this.mainDataSource));
        this.setCommodity(apiData);
        this.showHazmatWarning(this.mainDataSource.activeRow.get("hazmat_code") != null);
        const rateId = this.mainDataSource.activeRow.get("rate_id");
        const odRateId = this.mainDataSource.activeRow.get("orig_dest_rate_id");
        const exciseTaxCharges = this._getArrayAndNull(order, "excise_tax_charges");
        if (exciseTaxCharges != null && exciseTaxCharges.length > 0) {
            this.loadChildTableData(ratesLayout.tableTax, exciseTaxCharges, mode);
            ratesLayout.taxUtil.tableTaxOnContentsChanged();
        }
        ratesLayout.calculateRates(false, false, false).then(() => {
            RateUtil.setAutoRateContext(ratesLayout, rateId, odRateId);
        });
        if (apiData?._ctrl_party_txt) {
            this.layoutRates.textboxCtrlParty.textCombined.text = apiData._ctrl_party_txt;
        }
        const equipMatchDetails: ModelRow[] = order.get("equip_match_details", []);
        if (order.get("def_move_type", null) === 'A') {
            this.loadChildTableData(this.tableEquipmentRequirementsAsset, equipMatchDetails, mode);
        } else {
            this.loadChildTableData(this.tableEquipmentRequirementsBrokerage, equipMatchDetails, mode);
        }

        void this.defaultSalespersonFromCustomer();
    }

    private async setSalespersonValue(salespersonId: string): Promise<void> {
        if (StringUtil.isEmptyString(salespersonId) || this.mainDataSource?.activeRow == null) {
            return;
        }

        //no need to look up a salesperson the row already carries lookup data for
        const row = this.mainDataSource.activeRow;
        if (row.get("salesperson_id") === salespersonId && row.hasLookupModelData("salesperson_id")) {
            this.mainDataSource.displayDataInBoundComponent(this.textboxSalespersonId);
            this.textboxSalespersonId.text = salespersonId;
            return;
        }

        const salespersonRow = await new ModelSalesperson().searchSingle({id: salespersonId}, "lme/ar/SalespersonLookup");
        if (salespersonRow) {
            this.mainDataSource.activeRow.setValueAndLookupModelData("salesperson_id", salespersonId, salespersonRow);
            this.mainDataSource.displayDataInBoundComponent(this.textboxSalespersonId);
            this.textboxSalespersonId.text = salespersonRow.get("id", "");
        }
    }

    private async defaultSalespersonFromCustomer(customerRow?: ModelRow<any>): Promise<void> {
        const row = this.mainDataSource?.activeRow;

        if (row == null) {
            return;
        }

        //only honor an existing value on the load path; a customer change must win (matches classic)
        if (customerRow == null) {
            const existingSalespersonId = row.get("salesperson_id");
            if (!StringUtil.isEmptyString(existingSalespersonId)) {
                await this.setSalespersonValue(existingSalespersonId);
                return;
            }
        }

        const selectedCustomer = customerRow ?? this.textboxCustomerId.getFirstLookupModelData();
        const customerId = selectedCustomer?.get("id") ?? row.get("customer_id");
        if (StringUtil.isEmptyString(customerId)) {
            return;
        }

        const salespersonId = selectedCustomer?.get("salesperson_id", "");
        if (!StringUtil.isEmptyString(salespersonId)) {
            await this.setSalespersonValue(salespersonId);
            return;
        }

        const customerLookupRow = await Model.searchSingleRecord("lme/ar/customer", {id: customerId});
        const customerSalespersonId = customerLookupRow?.get("salesperson_id", "");
        if (!StringUtil.isEmptyString(customerSalespersonId)) {
            await this.setSalespersonValue(customerSalespersonId);
        } else if (customerRow != null) {
            this.clearSalespersonValue();
        }
    }

    private clearSalespersonValue(): void {
        const row = this.mainDataSource?.activeRow;

        if (row == null) {
            return;
        }

        row.setValueAndLookupModelData("salesperson_id", null, null);
        this.mainDataSource.displayDataInBoundComponent(this.textboxSalespersonId);
    }

    private getDFHandlingRequirementData(row: ModelRow) {
        const rows: ModelRow[] = [];
        const hrs = row.get("handling_requirements");
        if (hrs != null && hrs.length > 0) {
            hrs.forEach(data => {
                const hr = new ModelRow("lme/dispatch/brltl-order-hdr-x-fgp", false, data);
                rows.push(hr);
                this.layoutHandlingReqs.addHdrRow(hr);
            });
        }
        this.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data = rows;
    }

    private _displayDataCallback(dataSource: DataSource) {
        dataSource.boundComponents.forEach(component => {
            if (component.field != "recurring_order_id")
                dataSource.displayDataInBoundComponent(component);
        });
        this.creditValidator.customerId = this.mainDataSource.activeRow.get("customer_id");
        (this.layoutRates as OrderRates).setMaxTargetPayData();
    }

    private _getArrayAndNull(row: ModelRow, field: string): ModelRow[] {
        const result = row.get(field) || [];
        row.set(field, null);
        return result;
    }

    removeChildDataSources() {
        this.stopsTable.rows?.forEach(tableRow => {
            const orderStop = tableRow["orderStop"] as OrderStop;
            if (orderStop != null) {
                orderStop.commentsAndRefNbrs.sourceStopNote.parentDataSource = null;
                orderStop.commentsAndRefNbrs.sourceReferenceNumber.parentDataSource = null;
            }
        });
    }

    private loadChildTableData(table: Table, data: any[], mode: DataSourceMode) {
        const tableDataSource = table.dataSource;
        const modelRows = [];
        data.forEach((element: any) => modelRows.push(new ModelRow(tableDataSource.url, true, element)));
        tableDataSource.setRowsAndMode(mode, modelRows);
    }

    private checkRateExpiration(is_rate_expired: boolean) {
        if (is_rate_expired) {
            Snackbar.showWarningSnackbar("The rate on the recurring order you used to create this order has expired. "
                + "Rate information, freight charges and other charges have not been copied over.");
        }
    }

    clearEquipmentIds(event) {
        if (event.target.text == "") {
            this.textboxEquipmentTypeIdGeneral.text = "";
            this.textboxEquipmentTypeIdEquipment.text = "";
        }
    }

    async initializeAdd() {
        this.switchRecurringOrder.enabled = true;
        this.textboxRecurringOrderId.visible = false;
        this.textboxRecurringOrderId.visibleDuringAdd = false;
        this.textboxPreloadTrailerId.enabled = false;

        this.textboxHoldReason.visible = false;
        this.howManyOrders.value = 1;

        if (this["quote_id"]) {
            const response = await Api.search("lme/dispatch/convert-quote-to-order", {quote_id: this["quote_id"]});
            const orderApiData = response.data[0];
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                this.layoutRates.currencyTypeFromQuote = orderApiData["freight_charge_c"];
                this.layoutRates.textboxCurrencyType.defaultDataValue = orderApiData["freight_charge_c"];
            }
            this.loadFromApiData(orderApiData, DataSourceMode.ADD);
            if (this["create_spot"]) {
                this.activeRow.set("create_spot", true);
                this.activeRow.set("rate_source", "S");
                this.nextAction = NextAction.VIEW_ORDER;
                this.textboxCustomerId.required = false;
                this.textboxEquipmentTypeIdGeneral.required = true;
                // TODO: When asset orders is out of beta/EA, then the below code is not needed
                this.textboxDefMoveType.visible = false;
                this.activeRow.set("def_move_type", "B");
            }
        } else {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                this.layoutRates.textboxCurrencyType.defaultDataValue = await this.getTransactionalCurrency();
                await this.layoutRates.convertPricingCurrencies(await this.getTransactionalCurrency());
            }
            await this.addStopsToTable();
        }

        this.mainDataSource.addValidationListener(this._validateRespFilteringDuringAdd);
    }

    setCompanySettings(mode: DataSourceMode) {
        const companySettings = LMECompanySettings.getSingleton();
        this.textboxDefMoveType.visible = LMECompanySettings.get().show_movement_type;
        this.isAsset = AssetUtil.isAssetMoveType(this.sourceOrders);
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
        if (this.getInvokedFromEdi()) this.setEdiFields();
    }

    setDispatchSettings() {
        const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();

        if (dispatchControlSettings.enforce_billto == 'Y')
            this.textboxCustomerId.required = true;

        if (dispatchControlSettings.enforce_revenu_cod == 'Y')
            this.textboxRevenueCodeId.required = true;
    }

    onHoldLoadChange(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.textboxHoldReason.visible = event.target.checked;
        this.textboxHoldReason.required = event.target.checked;
        this.labelHoldBadge.visible = event.target.checked;
        if (!event.target.checked && this.activeRow) {
            this.activeRow.set("hold_reason", null);
            this.creditValidator.validate();
        }
    }

    textboxBlnumOnBlur(event: BlurEvent) {
        if (!this.mainDataSource.activeRow?.isNull("blnum") && !this.switchRecurringOrder.checked) {
            let source: DuplicateBOLSource;
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                source = DuplicateBOLSource.ADD;
            } else if (this.mainDataSource.mode == DataSourceMode.UPDATE && this.activeRow.getChangedData()["blnum"] != null) {
                source = DuplicateBOLSource.UPDATE;
            }

            if (source != null) {
                this.checkForDuplicateBOL(source)
                    .then((response) => {
                        if (!response.success)
                            this.textboxBlnum.validationWarning = response.reason;
                    });
            }
        }
    }

    async checkForDuplicateBOL(source: DuplicateBOLSource): Promise<SuccessFail> {
        return await this.checkDuplicateBOL.validate(source, this.saveButton);
    }

    doBeforeExecute(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.ADD) {
            this.stopsTable.rows.forEach(
                row => {
                    log.debug(row);
                    row.saveChanges();
                    log.debug(row.data);
                });
            log.debug("stops datasource length " + this.stopsTable.dataSource.data.length);
            if (this.howManyOrders.value > 1)
                this.mainDataSource.activeRow?.set("total_orders", this.howManyOrders.value);

            if (this.batchEntryMode && this.batchOrderEntry != null) {
                this.mainDataSource.activeRow.set("equipment_group_id", this.batchOrderEntry.equipmentGroup);
                this.mainDataSource.activeRow.set("equipment_items", this.batchOrderEntry.items);
            }
        }

        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            this.addBeforePostListeners(event);
        }
    }

    addBeforePostListeners(event: DataSourceExecutionEvent) {
        if (this.mainDataSource.activeRow != null) {
            const row = this.mainDataSource.activeRow;
            if (this.beforePostListener != null) {
                row.clearBeforePostListeners();
                this.beforePostListener = null;
            }
            this.beforePostListener = async postEvent => {
                if (!this.shouldOpenPaySlideout && this.mainDataSource.mode !== DataSourceMode.ADD && await this.shouldAskToResend()) {
                    this.shouldOpenPaySlideout = this.saveButton.onClose != null;
                    if (this.saveButton.saveAction == SaveAction.SAVE_AND_CLOSE) {
                        this.saveButton.onClose = null;
                        this.dataHeader.onExecute = () => {
                            return
                        };
                    }
                } else {
                    if (this.mainDataSource.activeRow.getChangedData()["def_move_type"] == "A") {
                        this.dataHeader.onExecute = async () => {
                            return
                        };
                    }
                }

                await this.creditValidator.rowUpdated(postEvent)
                if (!this.mainDataSource.activeRow?.isNull("blnum")) {
                    await this.checkDuplicateBOL.rowUpdatedBySource(postEvent, this.saveButton);
                }
                if (this.mainDataSource.mode === DataSourceMode.ADD ||  await OrderRerateLogic.promptForRerate(this)) {
                    this.carrierSelectionShouldRerate = true;
                    if (this.saveButton.saveAction == SaveAction.SAVE_AND_CLOSE) {
                        this.saveAction = this.saveButton.saveAction;
                    }
                }
                if (this.switchPostToLoadBoards.checked) {
                    this.postToLoadBoard = true;
                    if (!StringUtil.isEmptyString(this.movementTabs.currentTabId)) {
                        this.mainDataSource.activeRow.set("loadboard_post_movement_id", this.movementTabs.currentTabId);
                    } else {
                        this.mainDataSource.activeRow.set("loadboard_post_movement_id", this.availableMovementId);
                    }

                    if (!StringUtil.isEmptyString(this.textboxPnnRate.text) && StringUtil.isEmptyString(this.mainDataSource.activeRow.get("pnn_rate"))) {
                        this.mainDataSource.activeRow.set("pnn_rate", this.textboxPnnRate.text);
                        this.mainDataSource.activeRow.set("pnn_rate_type", "F");
                        this.textboxPnnRateType.text = "Flat";
                    }

                }
                if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                    const changedData = this.activeRow.getChangedData();
                    if (changedData["loadboard"] !== undefined && changedData["loadboard"] === 'N') {
                        const orderId = row.get("id");
                        const movementId = row.get("loadboard_post_movement_id") ? row.get("loadboard_post_movement_id") : row.get("movement.id");
                        this.cancelLoadBoardPosting(orderId, movementId);
                        this.mainDataSource.activeRow.set("loadboard_post_movement_id", "");
                        this.mainDataSource.activeRow.set("loadboard_posting_cancelled", false);
                    }

                }

            };
            row.addBeforePostListener(this.beforePostListener);
        }
    }

    async postOrderNavigateTo(): Promise<any> {
        let key = null;
        switch (this.nextAction) {
            case NextAction.VIEW_ORDER:  // view order list
                const duplicateOrders: Array<String> = this.mainDataSource?.activeRow?.get("duplicate_orders", []);
                const orderIds = [this.mainDataSource?.activeRow?.get("id"), ...duplicateOrders];
                if (orderIds.length > 1) {
                    return Navigation.navigateTo("lme/dispatch/OrderList?ids=" + orderIds.toString(), {newTab: false}).then(() => {
                        if (this.mainDataSource?.activeRow?.get("has_failed_orders")) {
                            this.creditValidator.showCustomerCreditValidationSnackbar("Unable to create all orders. " + this.textboxCustomerId.text +
                                " has exceeded their credit limit. " + (orderIds.length) +
                                " order(s) of " + this.howManyOrders.value + " were created.");
                        }
                    });
                } else {
                    key = this.mainDataSource?.activeRow?.get("id");
                    return OrderTabView.navigateTo({key, newTab: false, doAfterNavigate: () =>
                    {
                        if (this.mainDataSource?.activeRow?.get("has_failed_orders")) {
                            Snackbar.showWarningSnackbar("Unable to create all orders. " +
                                (this.mainDataSource?.activeRow?.get("failure_message") ? "\n\nReason: " + this.mainDataSource?.activeRow?.get("failure_message") : ""));
                        }
                    }});

                }

            case NextAction.NEW_ORDER:  // new order
                return Navigation.navigateTo("lme/dispatch/OrderTabView", { newTab: false });
            case NextAction.ASSIGN_CARRIER:  // assign carrier
                key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                return OrderTabView.navigateTo({
                    key,
                    orderId: this.activeRow.get("id"),
                    newTab: false,
                    additionalProps: {assignCarrier: true}
                });
            case NextAction.ASSIGN_DRIVER:  // assign driver
                const orderId = this.mainDataSource?.activeRow?.get("id");
                key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                return OrderTabView.navigateTo({
                    orderId: orderId,
                    key: key,
                    newTab: false,
                    additionalProps: {openPreassignment: true}
                });
            case NextAction.FIND_CARRIER:  // find carrier
                if (this.activeRow.getBoolean("is_brokerage_ltl")) {
                    key = this.mainDataSource?.activeRow?.get("id");
                    return OrderTabView.navigateTo({
                        key: key,
                        newTab: false,
                        additionalProps: {selectCarrier: true}
                    });
                } else {
                    key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                    return OrderTabView.navigateTo({
                        key: key,
                        orderId: this.activeRow.get("id"),
                        newTab: false,
                        additionalProps: {findCarrier: true}
                    });
                }
            default:
                return Promise.resolve();
        }
    }

    async executePostOrderAction() {
        if (this.switchPostToLoadBoardsNextAction.checked && !this.activeRow.getBoolean("is_brokerage_ltl")) {
            await this.displayPostToLoadBoardSlideout();
        }

        this.postOrderNavigateTo().then(() => {
            if (this.displayingFromToolbox || this.nextAction == NextAction.NEW_ORDER || this.nextAction == NextAction.ASSIGN_DRIVER) {
                const id = this.activeRow?.get("id", null);
                const toast = Toast.showToast(this.getOrderCreatedToast(id, "created", () => this.orderToastLinkOnClick(id, toast)), null, {targetPanel: ScreenStack.getPreviousToastTarget()});
            }
        })
    }

    /**
     * Checks if an order is an EDI inbound record (direction === "I")
     * @param orderId The order ID to check
     * @returns Promise<boolean> True if the order is EDI inbound, false otherwise
     */
    private async isEdiInboundOrder(orderId: string): Promise<boolean> {
        if (!orderId) return false;

        try {
            // Search for EDI billing details to get the direction
            const ediResponse = await Api.search("lme/datafusion/edi-billing-detail", { order_id: orderId });
            if (ediResponse && ediResponse.data && ediResponse.data.length > 0) {
                const ediRecord = ediResponse.data[0];
                const direction = ediRecord.get("direction");
                return direction === "I";
            }
        } catch (error) {
            // If there's no EDI record or an error, this is not an EDI order
            log.debug("No EDI record found for order:", orderId, error);
        }

        return false;
    }

    async doAfterExecute(event) {
        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            this.activeRow.clearBeforePostListeners();
            this.mainDataSource.addAfterSearchPlusChildrenListener(async () => {
                if (this.activeRow.getBoolean("is_brokerage_ltl", false) && this.carrierSelectionShouldRerate) {
                    const snackbarTarget = this.saveAction == SaveAction.SAVE_AND_CLOSE ? MainPageAccessor.router : ScreenStack.getSnackbarTarget();
                    this.buttonCarrierRates.busy = true;
                    this.carrierSelectionShouldRerate = false;
                    await CarrierSelection.findCarrierRates(this.activeRow.get("id"), null, null, () => this.mainDataSource.search({id: this.mainDataSource.activeRow.get("id")}), snackbarTarget).finally(() => {
                        this.buttonCarrierRates.busy = false;
                    });
                }
                if (this.shouldOpenPaySlideout) {
                    history.replaceState({additionalProps: {modifyPay: true}}, "", window.location.search);
                    this.panelMovementTab.clicked();
                    this.shouldOpenPaySlideout = false;
                }
                if (event.getAction() === DataSourceAction.ADD) {
                    this.executePostOrderAction();
                }

                // These fields are really part of the movement table, but they are used in the order's API data to update the movement.
                // They are no longer needed on the active row after posting, so set them to undefined to prevent reenabling the save button
                if (!this.mainDataSource.activeRow?.isNull("override_targetpay")) {
                    this.mainDataSource.activeRow.set("override_targetpay", undefined);
                }
                if (!this.mainDataSource.activeRow?.isNull("override_max_pay")) {
                    this.mainDataSource.activeRow?.set("override_max_pay", undefined);
                }
            });
        } else if (event.getAction() === DataSourceAction.SEARCH) {
            this.addLayoutDataLoadListener(() => {
                const overviewLayout = this.layoutOverview as OrderOverview;
                (overviewLayout.layoutStopProgress as StopProgress).setStopsTable(this.stopsTable);
                const status = this.mainDataSource.activeRow.get("movement.status", null);
                const onHold = this.mainDataSource.activeRow.get("on_hold", null);
                if (status != null && status != 'A') {
                    if (this.switchPostToLoadBoardsNextAction.checked)
                        this.switchPostToLoadBoardsNextAction.checked = false;
                    this.switchPostToLoadBoardsNextAction.enabled = false;
                    this.switchPostToLoadBoardsNextAction.disabledTooltip = "Movement must be available to post to load boards";
                }
                if (this.activeRow.getBoolean("is_brokerage_ltl", false)) {
                    this.switchPostToLoadBoardsNextAction.enabled = false;
                    this.switchPostToLoadBoardsNextAction.disabledTooltip = "Brokerage LTL orders cannot be posted to load boards";
                }
                if (status != null && status != 'A' && onHold != null && onHold == "Y" && this.switchPostToLoadBoardsNextAction.checked == true)
                    this.createPostToLoadBoardDialog().then(() => this.switchPostToLoadBoardsNextAction.checked = false);
                this._setEquipTypeOptions();
                (this.layoutImageTable as ImageTable).populateForRow("O", this.mainDataSource.activeRow.get("id"));
                this.switchRecurringOrder.checked = (null != this.mainDataSource.activeRow.get("recurring_order_id"));
                this.updateEquipTypeOptions();
                this.setOrderHistCommentsVisibility();
                this.checkReleaseToBillingAndDR();
                this.layoutRates.carrierPayLayout.configureForOrderEntry(this.mainDataSource);
                const commodityLookupData = this.textboxCommodityId.getFirstLookupModelData();
                const isHazmat = commodityLookupData ? commodityLookupData.getBoolean("is_hazmat", false) : false;
                this.showHazmatWarning(isHazmat);

                this.buttonSendBOL.visible = this.isBrokerageLtlCompany;
                this.switchTenderReplyTransmitted.visible = this.activeRow.getBoolean("reply_transmitted", false);
                if (this.isBrokerageLtlCompany)
                    this.getRatingDetails(this.mainDataSource.activeRow.get("id"));
                this.setupPreloadedTrailerLookup();
            });
        }
        this.buttonCarrierRates.visible = this.activeRow.getBoolean("is_brokerage_ltl", false) && !this.getInvokedFromEdi();
        const multiMoveOrder = this.activeRow?.getBoolean("multi_movement", false);
        this.layoutRates.layoutMovementRates.visible = !this.isAsset && !multiMoveOrder;
        this.layoutRates.panelOrderCarrierPay.visible = !this.isAsset && multiMoveOrder;
        this.setupDefMoveType();
        this.checkReleaseToBillingAndDR();
        const billingHistoryRecord = await new ModelBillingHistory().searchSingle({order_id: this.activeRow?.getString("id", null)});
        if (billingHistoryRecord) {
            // Check if this is an EDI inbound record and hide Invoice History tab accordingly
            const isEdiInbound = await this.isEdiInboundOrder(this.activeRow?.getString("id", null));
            this.panelInvoiceHistoryTab.visible = !isEdiInbound;
            if (isEdiInbound) {
                this.panelOrderTabs.remove(this.panelInvoiceHistoryTab, false);
                // Remove border from Movement tab since it's now the last tab
                if (this.panelMovementTab) {
                    this.panelMovementTab.borderRightWidth = 0;
                }
            } else {
                // Restore border on Movement tab for non-inbound records
                if (this.panelMovementTab) {
                    this.panelMovementTab.borderRightWidth = 1;
                }
            }
        } else {
            this.panelInvoiceHistoryTab.visible = false;
        }

        this.handleUrlSectionNavigation();
    }

    private handleUrlSectionNavigation() {
        const propsFromUrl = UrlUtil.getPropsFromUrl(document.URL);
        if (!propsFromUrl?.section) return;

        const sectionHandlers: Record<string, () => void> = {
            'stops': () => this.tabStops.scrollIntoView(),
            'movement': () => this.panelMovementTab.clicked()
        };

        sectionHandlers[propsFromUrl.section]?.();
    }

    setupDefMoveType() {
        const assetAndBrokerage = this.activeRow.get("def_move_type") == "L";
        const hasDisabledStatus = DISABLE_TEXTBOX_DEF_MOVE_TYPE_STATUSES.includes(this.activeRow.getString("status", null));
        this.textboxDefMoveType.enabled = !hasDisabledStatus && !assetAndBrokerage && this.activeRow.get("movement_count") == 1;
        if (!this.textboxDefMoveType.enabled) {
            this.textboxDefMoveType.disabledTooltip = hasDisabledStatus ? 'Movement Type cannot be altered for In Progress or Delivered Orders'
                : !(this.activeRow.get("movement_count") == 1) ? "Movement Type cannot be altered for Orders with multiple movements"
                    : "Movement Type cannot be altered for Asset & Brokerage Orders.";
        }
        if (assetAndBrokerage) {
            this.textboxDefMoveType.text = "Asset & Brokerage";
        }
    }

    async setPostingOverrideFields() {
        const orderRow = this.mainDataSource?.activeRow;
        const overrideArriveLate = orderRow.get("override_arrive_late");
        const overrideArriveEarly = orderRow.get("override_arrive_early");
        const overridePostingCityId = orderRow.get("override_posting_city_id") !== undefined ? orderRow.get("override_posting_city_id") : orderRow.get("stop_origin.city_id");
        if (overrideArriveEarly === undefined && overrideArriveLate === undefined) {
            this.textboxOverrideArriveEarly.text = orderRow.get("stop_origin.sched_arrive_early");
            orderRow.set("override_arrive_early", new Date(orderRow.get("stop_origin.sched_arrive_early")));
            if (orderRow.get("stop_origin.sched_arrive_late") !== undefined) {
                this.textboxOverrideArriveLate.text = orderRow.get("stop_origin.sched_arrive_late");
                orderRow.set("override_arrive_late", new Date(orderRow.get("stop_origin.sched_arrive_late")));
                this.setLoadBoardFieldsByOrder(overridePostingCityId, orderRow.get("id"));
            } else {
                this.textboxOverrideArriveLate.visible = false;
                this.andLabel.visible = false;
                this.setLoadBoardFieldsByOrder(overridePostingCityId, orderRow.get("id"));
            }
        } else {
            this.textboxOverrideArriveLate.visible = (overrideArriveLate !== undefined && this.switchPostToLoadBoards.checked);
            this.andLabel.visible = (overrideArriveLate !== undefined && this.switchPostToLoadBoards.checked);
            this.setLoadBoardFieldsByOrder(overridePostingCityId, orderRow.get("id"));
        }

    }

    async setLoadBoardFieldsByOrder(overridePostingCityId: string, orderId: string) {
        if (StringUtil.isEmptyString(orderId)) {
            return;
        }

        if (overridePostingCityId === undefined) {
            const response = await Api.post("lme/dispatch/order-movements", {
                "order_id": orderId,
                "brokerage_only": false
            })
            if (response) {
                const onlyAvailableMoves = this.filterAvailableMoves(response.data);
                if (onlyAvailableMoves != null && onlyAvailableMoves.length > 0) {
                    const movements = onlyAvailableMoves.map(move => new ModelRow(this.sourceMovement.url, false, move));
                    this.setLoadBoardDetailFields(movements[0]);
                }
            }
        }
    }

    getRatingDetails(orderId: string) {
        this.layoutRates.sourceRatingDetails.search({"order_id": orderId}).then(response => {
            this.layoutRates.sourceRatingDetailsSummary.search({"order_id": orderId}).then(response => {
                this.layoutRates.switchByFreightItem.checked = true;
                this.layoutRates.tableRevSummary.visible = false;
            });
        });
    }

    private showCarrierSelectionSlideout() {
        const orderId = this.mainDataSource.activeRow.get("id");
        CarrierSelectionStepper.showSlideout({
            orderId: orderId,
            orderRow: this.mainDataSource.activeRow,
            onClose: (cancelled, updatedData, hasSaved) => {
                if (updatedData?.hasChanged() || hasSaved) {
                    this.mainDataSource.search({id: orderId});
                    if (this.sourceMovement.url === updatedData._modelPath) {
                        if (this.parent != null && this.parent instanceof OrderTabView)
                            this.carrierAssignMentChanged();
                    }
                }
            }
        });
    }

    async doAfterModeChange(event: DataSourceModeChangeEvent) {
        this.creditValidator.customerId = null;
        this.makeSubjectOrderAdjustments();
        if (event.oldMode != DataSourceMode.ADD && event.newMode == DataSourceMode.ADD) {
            if (this["successAdd"]) {
                this.displayOrderCreatedToast(this["orderId"]);
            }
            if (!this.getInvokedFromEdi() && !this.displayingFromToolbox) {
                this.dataHeader.showSaveAndClose = false;
            }
            await this.initializeAdd();
        } else if (event.oldMode === DataSourceMode.NONE && event.newMode === DataSourceMode.UPDATE) {
            this.enforceActionPermissions(this.tabsetAddOrders);
            if (this.mainDataSource.activeRow?.get("credit_override_status") == "P" && this.mainDataSource.activeRow?.get("on_hold", null)) {
                this.enableOnHoldComponents(false);
            } else {
                this.creditValidator.customerId = this.mainDataSource.activeRow?.get("customer_id");
            }
        }
        if (event.newMode !== DataSourceMode.ADD)
            this.mainDataSource.removeValidationListener(this._validateRespFilteringDuringAdd);
        this.activeRow?.addAfterFieldUpdateListener((event) => {
            if (event.fieldName === "commodity_id" && this.textboxCommodityId.dataSource.actionInProgress == null) {
                this.commodityCodeChanged();
            } else if (event.fieldName === "bill_distance") {
                this.updateLabelBillingDistance();
            }
        })
        if (this.layoutRates.tabSuggestedPricing.visible) {
            this.sourceOrders.activeRow?.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
                if (event.oldValue != event.newValue) {
                    if (event.fieldName === "equipment_type_id") {
                        this.layoutRates.tabSuggestedPricing.expandCollapseTab(false, false)
                    }
                    if (event.fieldName === "bill_distance") {
                        this.layoutRates.tabSuggestedPricing.expandCollapseTab(false, false);
                    }
                }
            });
            this.stopsTable.addContentsChangedListener(() => this.layoutRates.tabSuggestedPricing.expandCollapseTab(false, false));
        }

        this.setCompanySettings(event.newMode);
        if (event.newMode === DataSourceMode.SEARCH) {
            this.textboxAutoRateId.dataSource = this.mainDataSource;
            this.textboxAutoRateId.visible = true;
            await this.setupSearch();
        }
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            CurrencyUtil.configureCurrencySelector(this.textboxOrderValueCurrency);
        }
    }

    private updateLabelBillingDistance() {
        this.layoutOverview.labelBillingDistance.color = this.layoutRates.textboxBillDistance.color;
        this.layoutOverview.labelBillDistanceUm.color = this.layoutRates.textboxBillDistance.color;
        const components = this.layoutOverview.panelBillingDistance.components;

        const isWarning = this.layoutOverview.labelBillingDistance.color === "warning";
        this.layoutOverview.billingDistanceDangerImage.visible = isWarning;
        for (const component of components) {
            const label = component as Label;
            if (!StringUtil.isEmptyString(label.field)) {
                label.color = isWarning ? "warning" : "default";
            }
            label.allowTooltip = !isWarning;
        }
    }

    setupBrltlDatasources() {
        this.sourceBrltlOrderHdrXFgp.parentDataSource = this.mainDataSource;
        this.sourceBrltlOrderHdrXFgp.parentSearchMode = ParentSearchMode.onParentDisplay;

        this.sourceBrltlBillingFreightGroup.parentDataSource = this.mainDataSource;
        this.sourceBrltlBillingFreightGroup.parentSearchMode = ParentSearchMode.onParentDisplay;

        this.sourceBrltlOrderFreightGroupItem.parentDataSource = this.mainDataSource;
        this.sourceBrltlOrderFreightGroupItem.parentSearchMode = ParentSearchMode.onParentDisplay;
    }

    makeSubjectOrderAdjustments() {
        if (isSpotOrder(this.activeRow)) {
            this.textboxCustomerId.required = false;
        } else if (this.activeRow?.get("subject_order_status") == null) {
            this.tabSpotOrder.visible = false;
        }
    }

    enforceActionPermissions(component?: Layout | TableRow | Tabset) {
        if (this.mainDataSource.activeRow == null) return;
        const isUpdateOrdersAfterBilledDenied: boolean = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER);
        const isBilled: boolean = !this.mainDataSource.activeRow.isNull("bill_date");
        const isTransferred: boolean = !this.mainDataSource.activeRow.isNull("xferred2billing_dt");

        const isUpdateOrdersAfterDeliveredDenied: boolean = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER);
        const isDelivered: boolean = this.mainDataSource.activeRow.get("status", null) == "D";

        if ((isUpdateOrdersAfterBilledDenied && (isBilled || isTransferred)) ||
            (isUpdateOrdersAfterDeliveredDenied && isDelivered)) {
            const ignoreList = ['layoutImageTable', 'tableImages', 'buttonMoveDetails', 'tabsetAddOrders', 'tabsetStopNotesRefNos', 'tabImages','panelStopCommentsReferenceNumbers','stopsTable'];
            const enableDatafusion = ['panel8', 'panel4', 'tabOverview', 'layoutOverview', 'panelOrderInfo', 'panelDataFusion',
                'layoutOrderDataFusionSummary', 'labelInboundTenderCount', 'labelOutboundTenderCount',
                'labelOutboundShipmentStatusCount', 'labelInboundShipmentStatusCount', 'imageIntercompanyError', 'imageIntercompany'];
            const rootComponent = component ?? this;
            rootComponent.forEveryChildComponent(comp => {
                if (ignoreList.includes(comp.id))
                    return;

                //enables Order DataFusion slide out
                if (enableDatafusion.includes(comp.id)) {
                    comp.enabled = true;
                } else if (!(comp instanceof Panel)|| (comp instanceof Location) || comp.hasClickListeners()){
                    comp.enabled = false;
                }

                 if (comp instanceof Table) {
                        const nestedTable: Table = comp as Table
                        nestedTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
                            const row: TableRow = (event as TableRowDisplayEvent).getTableRow();
                            row.forEveryChildComponent(child => {
                                if (!(child instanceof Label) || child.hasClickListeners()) child.enabledDuringUpdate = false;
                            });

                        });
                    }

            });
        }
    }

    async doBeforeModeChange(event: DataSourceModeChangeEvent) {
        const isAdd = event.newMode === DataSourceMode.ADD;
        const isUpdate = event.newMode === DataSourceMode.UPDATE;
        const isSearch = event.newMode === DataSourceMode.SEARCH;
        const isNone = event.newMode === DataSourceMode.NONE;
        const allowBatchEntry = getDispatchControlBoolean("allow_batch_entry", true);

        this.hideSuffix = !isAdd && !isSearch;
        this.panelDataHeaderUpdateOnly.visible = isUpdate;
        this.dataheaderOrders.addlPanelLeft.visible = true;
        this.textboxOrderNumber.visible = isAdd || isUpdate;
        this.buttonUsers.visible = await ResponsibilityUtil.isFilterEnabled("order_filter") && (isSearch || isUpdate);
        this.buttonAutoMatch.visible = isAdd || isUpdate;
        this.buttonShare.visible = isAdd || isUpdate;

        this.tabNextActions.visible = isAdd;
        this.tabDataFusion.visible = isUpdate;
        this.tabBackOffice.visible = isUpdate || isSearch;
        this.tabOverview.visible = this.tabImages.visible = isUpdate || isNone;

        const company = LMECompanySettings.getSingleton();
        this.panelBatchOrder.visible = isAdd && !company.isBrokerageOnly() && !company.isBrokerageLtl() && allowBatchEntry;

        if (isSearch) {
            this.hideComponentsForSearch();
        }
        if (!isSearch && this.stopsTable.dataSource == null) {
            this.stopsTable.dataSource = this.sourceStop;
        }
        if (isUpdate) {
            this.title = "Order Number " + this.mainDataSource.activeRow?.get("id");
            this._setPreferredVendorLookupModelData(this.mainDataSource.activeRow);
        }
        if (isAdd) {
            if (this["create_spot"]) {
                this.title = "New Spot Order";
                this.hideSuffix = true;
            } else if (this.displayingFromToolbox) {
                this.panelHowManyOrders.visible = false;
                this.panelNextActionButtons.visible = false;
            }
        }
    }

    async setupSearch() {
        this.recursiveSetAllDataSourcesToMode(DataSourceMode.SEARCH);
        this.stopsTable.dataSource = null;
        const tbModifier = tb => {
            //this is a temporary workaround to allow users to use < and > on numeric fields in search
            const isNum = SEARCH_OVERRIDE_DISPLAY_TYPES.includes(tb.displayType);
            if (isNum) tb.displayType = DisplayType.STRING

            tb.enabled = true
            tb.printable = false
            tb.nullDisplayValue = ""
            tb.color = ""
            tb.clear()
        }
        this.findComponentsByType(Textbox).forEach(tbModifier);
        this.layoutRates.searchTbModifier = tbModifier;
        this.getRecursiveChildren().forEach(c => {
            c.enabled = true
            c.required = false
        });

        const segAllocControl = await Model.searchSingleRecord("lme/settlement/seg-alloc-control", null, ["is_active", "basis"]);
        this.layoutRates.textboxSegAllocCode.visible =
            segAllocControl?.get("is_active", false) && SEG_ALLOC_BASIS.includes(segAllocControl?.get("basis", null));
    }

    hideComponentsForSearch() {
        this.findComponentsByType(Switch).forEach(s => {
            if (!s.field) s.visible = false
        });
        this.tabStops.visible = false;
        this.tabBrltlFreightItems.visible = this.tabAccessorials.visible = false;
        this.panelOrderTabs.visible = false;
        this.layoutRates.tabSuggestedPricing.visible = false;
        this.layoutRates.textboxCtrlParty.visible = false;
        this.layoutRates.layoutMovementRates.visible = false;
        this.findComponentsByType(Button).forEach(button => button.visible = false);
        this.tabDataFusion.visible = false;
    }

    private async addStopsToTable() {
        if (this.stopsTable.dataSource.data?.length === 0) {
            await this.addNewStopToTable();
            await this.addNewStopToTable();
            log.debug("stops length " + this.stopsTable.dataSource.data.length)
        }
        this.tabsetAddOrders.expandAll();
    }

    public async addNewStopToTable(): Promise<TableRow> {
        let addRowResult: TableAddRowResult;
        const newRowData = await this.stopsTable._createNewRowData();
        if (newRowData != null) {
            newRowData.set("stop_type", this.stopsTable.dataSource.data.length > 0 ? "SO" : "PU");
            addRowResult = this.stopsTable.addRow(newRowData, {mode: TableRowMode.ADD}, {
                display: true,
                addToData: true
            });
        }
        return addRowResult.row;
    }

    commodityCodeChanged() {
        if (this.activeRow != null) {
            const commodityCleared = this.activeRow.isNull("commodity_id");
            const lookupData = commodityCleared ? null : this.textboxCommodityId.getFirstLookupModelData();
            const hazmatModelRow = commodityCleared ? null : new ModelRow(this.textboxHazmatCode.lookupModel, false, {id: lookupData?.get("hazmat_number")});

            this.showHazmatWarning(lookupData?.get("hazmat_number") != null);
            this.activeRow.setLookupModelData("hazmat_code", hazmatModelRow);
            this.activeRow.set("hazmat_code", lookupData?.get("hazmat_number"));
            this.activeRow.set("commodity", lookupData?.get("descr"));
            this.activeRow.set("hazmat", lookupData?.get("hazmat", "N"));
            this.activeRow.set("temperature_min", lookupData?.get("temperature_min"));
            this.activeRow.set("temperature_max", lookupData?.get("temperature_max"));
            this.activeRow.set("placard_required", lookupData?.get("placard_required", null));
        }
    }

    getBRLTLData() {
        const orderData: BRLTLOrder = {
            order_type_id: this.activeRow.get("order_type_id"),
            revenue_code_id: this.activeRow.get("revenue_code_id"),
            order_mode: this.activeRow.get("order_mode")
        }
        return orderData
    }

    showHazmatWarning(hazmat: boolean) {
        this.panelCommodityHazmat.borderWidth = hazmat ? 1 : 0;
        this.panelCommodityHazmat.borderColor = hazmat ? "error" : "";
        this.labelHazmatCommodity.visible = hazmat;
    }

    textboxAppliesToOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (!event.userInitiatedChange) return;
        const appliesTo: Textbox = event.target as Textbox;
        const equipType = TableRow.getContainingTableRow(appliesTo).findComponentById("textboxMatchEquipmentTypeIdAsset") as Textbox;
        equipType.text = null;
        equipType.updateBoundData(equipType.boundRow, this.mainDataSource.mode);
    }

    textboxMatchEquipmentTypeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (AssetUtil.isAssetMoveType(this.sourceOrders)) {
            event.filter.applies_to = "in Y,X,D,T,L";
        } else {
            event.filter.applies_to = "in Y,X,D";
        }
    }

    buttonAddAnotherStopOnClick(event: ClickEvent) {
        this.addNewStopToTable().then(row => row.scrollIntoView());
    }

    buttonAddAnotherStopOnDataDisplay(event: DataDisplayEvent) {
        (event.target as Button).setProps({...(event.target as Button).allProps, "color": "primary"});
    }

    //** This is an event handler for stopsTable onRowBeforeSave */
    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data;
        const orderStopLayout: OrderStop = event.target["orderStop"] as OrderStop;
        modelRow.set("stop_notes", orderStopLayout.commentsAndRefNbrs.sourceStopNote?.data || []);
        modelRow.set("stop_reference_numbers", orderStopLayout.commentsAndRefNbrs.sourceReferenceNumber?.data || []);
        const changedData = modelRow.getChangedData();
        if (AppointmentUtil.apptConfirmCheck(changedData, modelRow))
            orderStopLayout.getAppointmentChangedRow();
        else {
            modelRow.set("appointment_change", null);
        }
    }

    textboxRecurringOrderIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    /** This is an event handler for the onRowDisplay event of stopsTable.  */
    stopsTableOnRowDisplay(event) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.allowDelete = tableRow.index != 0 && tableRow.data?.get("stop_type") != "SD" && tableRow.data?.get("stop_type") != "SP";
        const labelSequence = (tableRow.findComponentById("labelSequence")) as Label;
        labelSequence.text = (tableRow.index + 1).toString();
        const buttonManageStops = (tableRow.findComponentById("buttonManageStops")) as Panel;
        buttonManageStops.visible = this.sourceStop.mode != DataSourceMode.ADD && !PermissionsUtil.isUserDeniedLayout("lme/dispatch/ManageStops");

        const layoutOrderStop = tableRow.findComponentById("layoutOrderStop") as OrderStop;

        const buttonAddLocation = tableRow.findComponentById("buttonAddLocation") as Button;
        buttonAddLocation.visible = this.mainDataSource.mode === DataSourceMode.ADD;
        this.enforceActionPermissions(tableRow);
        this.sourceStop.preventChangeNotifications = true;
        tableRow["orderStop"] = layoutOrderStop;
        layoutOrderStop.calculateRates = (checkDistance) => this.checkStopsForDistanceChange(checkDistance).then(() => {
            this.layoutRates.calculateRates(false, false)
        });
        layoutOrderStop.commodityChanged = () => {
            this.setCommodity();
        }
        layoutOrderStop.invokedFromEDI = this.getInvokedFromEdi();
        layoutOrderStop.initialize(false, tableRow, this.sourceOrders, this.sourceStop);
        layoutOrderStop.loadingRecurringOrder = this.loadingRecurringOrder;
        layoutOrderStop.panelAdditionalDetails.visible = true;
        if (this.getInvokedFromEdi()) {
            layoutOrderStop.setStopDateTypeForEdi();
        }
        layoutOrderStop.setFreeFormVisibility();
        if (this.getInvokedFromEdi() || this.loadingRecurringOrder || (this["quote_id"])) {
            layoutOrderStop.doAfterLocationIdChanged(this["quote_id"], this.hasPortalContactInfo(tableRow.data));
        }
        if (tableRow.index == 0) {
            if (this.isBrokerageLtlCompany) {
                this.layoutShipperPONums.initialize({
                    "stopSource": this.sourceStop,
                    "firstStopRow": tableRow.data
                });
            }
            layoutOrderStop.switchPalletsRequired.displayData(this.sourceOrders.activeRow, null, 0);
            layoutOrderStop.textboxPalletsHowMany.displayData(this.sourceOrders.activeRow, null, 0);
        }
        if (layoutOrderStop.commentsAndRefNbrs.enabled == false) {
            const commentsTable = layoutOrderStop.commentsAndRefNbrs.tableStopComments;
            commentsTable.allowEdit = false;
            const tableRefNbrs = layoutOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers;
            tableRefNbrs.allowEdit = false;
            layoutOrderStop.commentsAndRefNbrs.tabsetStopNotesRefNos.expandAll();
        }
        this.sourceStop.preventChangeNotifications = false;
        layoutOrderStop.checkLocationIsTrailerPool = (usePool: boolean) => {
            this.setPreloadedTrailerLookupInfo(usePool)
        };
        this.enableCaptions(layoutOrderStop);

    }

    private enableCaptions(comp: any) {
        comp.forEveryChildComponent(c => {
            let textbox: Textbox;
            if (c instanceof Textbox) {
                textbox = c;
            } else if (c instanceof Location || c instanceof CityState) {
                textbox = c.textCombined;
            }
            if (textbox) {
                textbox.captionVisibleInsideTable = true;
                textbox.captionVisible = true;
            }
        });
    }

    private revenueCodeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": newValue,
                "order_type_id": this.mainDataSource.activeRow.get("order_type_id"),
                "current_mode": this.mainDataSource.activeRow.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.mainDataSource.activeRow.set("order_mode", newMode);
            });
    }

    private hasPortalContactInfo(data: ModelRow) {
        //This is needed to check if contact info was filled from the portal.
        //If it was filled from portal, we should not automatically update
        //if the location ID has contacts.
        return !data.isNull("phone") || !data.isNull("contact_name");
    }

    private orderTypeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": this.mainDataSource.activeRow.get("revenue_code_id"),
                "order_type_id": newValue,
                "current_mode": this.mainDataSource.activeRow.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.mainDataSource.activeRow.set("order_mode", newMode);
            });
    }

    async displayOrderUsersSlideout() {
        const responsibleRoleTypeEntry = "E";
        let enableCommissions = false;
        let enableCustomerCommissions = false;
        const payrollControlResponse = await Model.searchSingleRecord("lme/dispatch/payroll-control")
        enableCommissions = payrollControlResponse?.getBoolean("enable_commissions");

        const agentAccessResponse = await Model.searchSingleRecord("lme/dispatch/agent-access-ctrl")
        enableCustomerCommissions = agentAccessResponse?.getBoolean("auto_assign_cus_commissions");

        const usersResponsibilitySlideout = UsersResponsibilitySlideout.builder()
            .layoutName("lme/dispatch/OrderUsers")
            .title(this.getOrderUsersSlideoutTitle())
            .idFilter(this.getOrderUsersIdFilter())
            .parentMainDataSource(this.sourceOrders)
            .parentRespsonsibleHistDataSource(this.sourceResponsibleHist)
            .includeCommissions(true)
            .commissionRoleType(responsibleRoleTypeEntry)
            .isBillingCreditRecord(false)
            .enableCommissions(enableCommissions)
            .enableCustomerCommissions(enableCustomerCommissions)
            .build();
        usersResponsibilitySlideout.show();
    }

    private getOrderUsersSlideoutTitle(): string {
        const name = this.sourceOrders.activeRow?.get("id");
        return "Order Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getOrderUsersIdFilter(): any {
        return this.sourceOrders.activeRow?.get("id") ?? null;
    }

    sourceOrdersAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set these fields when in search mode because they aren't bound fields for the main layout
            for (const field of ORDER_USER_SHARED_FIELDS) {
                const fieldValue = dataSource.searchRow.get(field);
                row.set(field, fieldValue);
            }

            //have to add hierarchy search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceResponsibleHist?.data)) {
                return;
            }
            row.addLinkedModel({model: this.sourceResponsibleHist.url, rows: [this.sourceResponsibleHist.data[0]]});
        }
    }

    private async setPostingCityState(cityId: string) {
        await Model.searchSingleRecord("common/city", {id: cityId}).then(
            (cityRow: ModelRow) => {
                if (cityRow) {
                    const defaultData = new ModelRow(this.sourceOrders.layoutName, false, {
                        override_posting_city_id: cityRow.get("id"),
                        posting_city_name: cityRow.get("name"),
                        posting_state_id: cityRow.get("state_id"),
                        posting_zip_code: cityRow.get("zip_code")
                    });
                    this.mainDataSource.activeRow.set("override_posting_city_id", cityRow.get("id"));
                    this.postingCityState.displayData(defaultData, null, 0);
                    this.postingCityState.tooltipDynamicContent =
                        this.postingCityState.makeCityStateTooltipContent(cityRow.get("id"));
                }
            }
        );
    }

    private displayPostToLoadBoardSlideout(): Promise<void> {
        return new Promise<void>((resolve) => {
            const layout = Layout.getLayout("lme/dispatch/PostToLoadBoards", {
                maxWidth: 1000,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as PostToLoadBoards;

            const erd = new EditRowDecorator({
                title: "Post to Load Boards - Order " + this.activeRow.get("id"),
                layout: layout,
                fillVerticalSpace: true,
                width: 1000,
                overlayProps: {closeOnClickOff: false, greyedBackground: true},
                data: this.activeRow,
                onSave: () => layout.postDataSources().then(() => resolve()),
                doAfterSlideIn: () => {
                    erd.multiButton.dataSource = layout.mainDataSource;
                    erd.multiButton.busyWhenDataSourceBusy = true;
                    layout.setDefaultsByStop(this.sourceStop.data[0], null, null, false);
                    layout.setDefaultById(this.activeRow?.get("origin_stop_id"), null, null, false);
                },
                onClose: (cancelled: boolean) => {
                    erd.slideOut().then(() => {
                        if (cancelled) {
                            resolve();
                        }
                    });
                }
            });
        });
    }

    textboxPnnLoadboardUserIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    switchRecurringOrderOnChange(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.textboxRecurringOrderId.visible = this.switchRecurringOrder.checked;
        this.textboxRecurringOrderId.visibleDuringAdd = this.switchRecurringOrder.checked;
        this.textboxRecurringOrderId.enabledDuringAdd = this.switchRecurringOrder.checked;
        if (this.mainDataSource.mode == DataSourceMode.ADD && !this.mainDataSource.activeRow?.isNull("blnum") && !this.switchRecurringOrder.checked)
            this.checkForDuplicateBOL(DuplicateBOLSource.ADD);
    }

    async switchBatchOrderOnChange(event) {
        if (this.mainDataSource.mode !== DataSourceMode.ADD) return;

        this.labelOrderView.text = this.switchBatchOrder?.checked ? "Add Batch Order" : "Add Order";
        if (this.switchBatchOrder?.checked) {
            if (!this.mainDataSource.activeRow?.isNull("recurring_order_id") &&
                this.hasSplitPoint()) {
                this.switchBatchOrder.checked = false;
                CommonDialogs.showError("Cannot use batch entry with a recurring order that has split points.");
                return;
            }
            await Api.post("lme/dispatch/create-empty-equipment-group").then(async output => {
                this.batchOrderEntry.equipmentGroup = output?.data[0].equipment_group_id;
                this.getEquipmentGroupData(this.batchOrderEntry.equipmentGroup);
                this.batchEntryMode = true;
            });
        } else {
            this.mainDataSource.activeRow.set("equipment_group_id", null);
            this.batchEntryMode = false;
        }
    }

    private hasSplitPoint(): boolean {
        const stopData = this.sourceStop?.data ?? [];
        return stopData.some(stop => ["SP", "SD"].includes(stop.get("stop_type")));
    }

    private editSaveAndCloseButton: SaveButton = SaveButton.create({
        id: "saveAndCloseButton",
        caption: "Save & Close",
        rowBreak: false,
        dataSource: this.layoutBatchOrderRouting.sourceEquipmentGroup,
        onClick: async (event: ClickEvent) => {
            const validationErrors: string = await this.layoutBatchOrderRouting.validateLayout();
            if (validationErrors) {
                Snackbar.showWarningSnackbar(validationErrors);
                return;
            }
            this.batchOrderEntry.items = this.layoutBatchOrderRouting.tableBatchOrderRouting.data;
            this.layoutBatchOrderRouting.slideOut();
            this.switchBatchOrder.enabled = false;
            const companyTypeBoth = CompanySettings.getSingleton().isBoth();
            if (companyTypeBoth) {
                this.textboxDefMoveType.setSelectedItemFromValue("A");
                this.textboxDefMoveType.enabled = false;
                this.mainDataSource.activeRow.set("def_move_type", "A");

                // Iterate through the item and get the tractor_id
                for (const item of this.batchOrderEntry.items) {
                    if (item.get("type_sequence") === 0) {
                        this.labelTractorId.text = `Tractor: ${item.get("tractor_id")}  Driver: ${item.get("driver_id")}`;
                    }
                }
            }
        }
    })

    async getEquipmentGroupData(equipmentGroupId: string) {
        new SlideoutDecorator({
            layout: this.layoutBatchOrderRouting,
            width: '1200px',
            title: "Add Batch Order Assignment",
            displayOverlay: true,
            backgroundColor: "defaultBackground",
            fillVerticalSpace: true,
            addlComponents: this.editSaveAndCloseButton,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            onClose: async (cancelled: boolean) => {
                if (!cancelled)
                    this.layoutBatchOrderRouting.slideOut();

                if (this.layoutBatchOrderRouting.sourceEquipmentGroup.hasChanged(true)) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = {
                        yesButtonCaption: "Leave",
                        noButtonCaption: "Stay"
                    };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true)
                        return;
                }
                this.layoutBatchOrderRouting.slideOut();
                this.switchBatchOrder.checked = false;
            },
            layoutLoadListeners: async () => {
                await this.layoutBatchOrderRouting.tableBatchOrderRouting.dataSource.search({ id: equipmentGroupId }).then(() => {
                    this.layoutBatchOrderRouting.mainDataSource.mode = DataSourceMode.NONE;
                    this.layoutBatchOrderRouting.equipmentGroupId = equipmentGroupId;
                });
            }
        });
    }

    public static navigateTo(id: string, newTab = true): void {
        OrderTabView.navigateTo({key: id, newTab: newTab});
    }

    private postOrderActionButtonClick(event: ClickEvent) {
        const button = event.target as Button;
        this.panelNextActionButtons.forEveryChildComponent((component) => {
            if (component._element.nodeName === "BUTTON") {
                component.color = "subtle.darker";
                component.backgroundColor = "primary.reverse"
            }
        });
        button.color = "primary.reverse";
        button.backgroundColor = "primary";
    }

    /** This is an event handler for the onClick event of buttonAssignCarrier.  */
    buttonAssignCarrierOnClick(event: ClickEvent) {
        this.nextAction = NextAction.ASSIGN_CARRIER;
        if (this.switchPostToLoadBoardsNextAction.checked)
            this.switchPostToLoadBoardsNextAction.showAdhocTooltip("Post to load board unavailable when Assign Carrier is selected for the next action.", {
                warning: true,
                timeout: 6000
            });
        this.switchPostToLoadBoardsNextAction.checked = false;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonAssignDriver.  */
    buttonAssignDriverOnClick(event: ClickEvent) {
        this.nextAction = NextAction.ASSIGN_DRIVER
        this.switchPostToLoadBoardsNextAction.checked = false;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonFindCarrier.  */
    buttonFindCarrierOnClick(event: ClickEvent) {
        this.nextAction = NextAction.FIND_CARRIER;
        this.postOrderActionButtonClick(event);
    }

    async buttonCarrierRatesOnClick(event: ClickEvent) {
        if (OrderRerateLogic.shouldRerate(this)) {
            Snackbar.showWarningSnackbar("Order has unsaved changes. Please save before viewing carrier rates.");
        } else {
            this.showCarrierSelectionSlideout()
        }
    }

    /** This is an event handler for the onClick event of buttonEnterNewOrder.  */
    buttonEnterNewOrderOnClick(event: ClickEvent) {
        this.nextAction = NextAction.NEW_ORDER;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonViewOrder.  */
    buttonViewOrderOnClick(event: ClickEvent) {
        this.nextAction = NextAction.VIEW_ORDER;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onChange event of howManyOrders.  */
    howManyOrdersOnChange(event: ChangeEvent) {
        if (event.newValue > 1 && (this.nextAction === NextAction.FIND_CARRIER || this.nextAction === NextAction.ASSIGN_CARRIER))
            this.buttonViewOrder.clicked();

        this.buttonAssignCarrier.enabled = event.newValue <= 1;
        this.buttonAssignCarrier.disabledTooltip = event.newValue > 1 ? "Assign carrier action unavailable when orders to add is greater than 1" : "";
        this.buttonFindCarrier.enabled = event.newValue <= 1;
        this.buttonFindCarrier.disabledTooltip = event.newValue > 1 ? "Find carrier action unavailable when orders to add is greater than 1" : "";
        if (event.newValue > 1) {
            this.switchPostToLoadBoardsNextAction.checked = false;
        }
        this.switchPostToLoadBoardsNextAction.enabled = event.newValue <= 1;
        this.switchPostToLoadBoardsNextAction.disabledTooltip = event.newValue > 1 ? "Post to load boards unavailable when orders to add is greater than 1" : "";
    }

    stopTableOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const thisOrderStop = tableRow["orderStop"] as OrderStop;
            const editOrderStop = rowDecorator.layout as OrderStop;
            if (this.sourceOrders.isAddingOrUpdating()) {
                editOrderStop.commentsAndRefNbrs.tableStopComments.dataSource.mode = DataSourceMode.UPDATE;
                editOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers.dataSource.mode = DataSourceMode.UPDATE;
            }
            editOrderStop.mainDataSource.activeRow?.set("stop_notes", thisOrderStop?.commentsAndRefNbrs.tableStopComments.data);
            editOrderStop.mainDataSource.activeRow?.set("reference_numbers", thisOrderStop?.commentsAndRefNbrs.tableStopReferenceNumbers.data);
            editOrderStop.initialize(true, tableRow, this.sourceOrders, this.sourceStop);
            editOrderStop.slideOutTableRow = tableRow;

            rowDecorator.onSave = this.stopSlideoutOnSave(rowDecorator, tableRow);
            rowDecorator.btnDelete.visible = false;
            rowDecorator.layout.width = window.innerWidth * .75;
            rowDecorator.labelTitle.text = "Order Entry Stop Details - " + this.title;
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    stopSlideoutOnSave(rowDecorator: EditRowDecorator, tableRow: TableRow): (updatedData: ModelRow | any) => void {
        const orderStop = (rowDecorator.layout as OrderStop);
        const thisOrderStop = tableRow["orderStop"] as OrderStop;
        return async (_updatedData: ModelRow | any) => {
            tableRow.saveChangesFromEdit(tableRow.data);
            thisOrderStop.textboxLocationId["_lookupModelDataForUpdate"] = orderStop.textboxLocationId["_lookupModelDataForUpdate"]; // syncs lookupModelDataForUpdate which is used in updateBoundData in Location.ts
            if (tableRow.index == 0) {
                this.sourceOrders.activeRow.set("pallets_how_many", orderStop.textboxPalletsHowMany.text);
                this.sourceOrders.activeRow.set("pallets_required", true === orderStop.switchPalletsRequired.checked ? "Y" : "N");
            }
            orderStop.commentsAndRefNbrs.saveChangesFromEdit(tableRow["orderStop"].commentsAndRefNbrs);
            thisOrderStop.textboxLocationId.displayData(tableRow.data, null, 0);
            thisOrderStop.setLocationTextAfterLocation(thisOrderStop.textboxLocationId.textCombined, tableRow.data, "location_id");
            thisOrderStop.calculateRates();
            thisOrderStop.activeRow.set("initial_appointment_change", null); // reset in scenario if user exits slideout and makes another update on main screen
        };
    }

    viewLoadTenders() {
        const id = this.mainDataSource.activeRow?.data?.["id"];
        log.debug("id", id);
        if (id) {
            const layout = Layout.getLayout("lme/datafusion/OrderLoadTenderHistory", {
                maxWidth: 900,
                width: window.innerWidth * .60,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as OrderLoadTenderHistory;

            new SlideoutDecorator({
                layout: layout,
                title: "Tenders - Order " + id,
                fillVerticalSpace: true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({order_id: id})
            });

            layout.orderId = id;
        }
    }

    viewShipmentStatus() {
        const id = this.mainDataSource.activeRow?.data?.["id"];
        const companyId = this.mainDataSource.activeRow?.data?.["company_id"];
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                //maxWidth: 900,
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;

            new SlideoutDecorator({
                layout,
                title: `Order Number ${id}`,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                onClose: () => {
                    this.mainDataSource.search({id: id});
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.sourceEdistatus.search({order_id: id});
                    layout.sourceEdiOrder.search({order_id: id});
                    layout.sourceEdiMovementOrder.search({order_id: id, company_id: companyId});
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const dataLayout = layout.outboundDataSource.data;
                            dataLayout?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                }
                            });
                        }
                    });
                },
                doAfterSlideOut: (decorator: SlideoutDecorator) => {
                    this.mainDataSource.search({id: id});
                }
            });
        }
    }

    set nextAction(nextAction: NextAction) {
        this._nextAction = nextAction;
    }

    get nextAction(): string | (() => void) {
        return this._nextAction;
    }

    /** This is an event handler for the onChange event of switchPostToLoadBoards.  */
    switchPostToLoadBoardsOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.switchPostToAllServices.visible = false;
            this.setLoadboardFieldVisibility(false);
            return;
        }

        const urlParams: any = UrlUtil.getPropsFromUrl(window.location.href);
        const switchPostToLoadBoards = event.target as Switch;

        this.switchPostToAllServices.visible = switchPostToLoadBoards.checked;

        if (event.userInitiatedChange) {
            const onHold = this.mainDataSource.activeRow.get("on_hold");
            if (event.newValue && (this.nextAction === NextAction.ASSIGN_CARRIER || this.nextAction === NextAction.ASSIGN_DRIVER))
                this.buttonFindCarrier.clicked();
            if (switchPostToLoadBoards.checked && onHold == "Y")
                this.createPostToLoadBoardDialog().then(() => switchPostToLoadBoards.checked = false);

            if (switchPostToLoadBoards.checked) {
                this.switchPostToAllServices.checked = true;
            } else {
                this.textboxPreferredVendors.visible = false;
                this.textboxPreferredVendors.required = false;
            }
        }
        if (switchPostToLoadBoards.checked && (urlParams?.mode === DataSourceMode.UPDATE || this.mainDataSource.mode === DataSourceMode.UPDATE)) {
            this.setLoadboardFieldVisibility(true);
            this.switchPostToAllServices.checked = StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow.get("all_vendors_enabled"), "Y");

            this._setEquipFields();
            this.setupMovementTabs(event.userInitiatedChange);
            if (event.userInitiatedChange) {
                this.setPostingOverrideFields();
            }
            this.mainDataSource.displayDataInBoundComponents(); // Add this line
        } else {
            this.setLoadboardFieldVisibility(switchPostToLoadBoards.checked);
            this.textboxPreferredVendors.visible = false;
        }

        this.layoutMovementTabs.visible = switchPostToLoadBoards.checked;
        this.movementTabs.visible = switchPostToLoadBoards.checked;

        this.panelLoadBoardFields.visible = switchPostToLoadBoards.checked;


    }

    private async _setEquipFields() {
        if (this.switchPostToLoadBoards.checked) {
            const row = this.mainDataSource.activeRow;
            if (StringUtil.isEmptyString(row.get("pnn_trailer_length")) && !StringUtil.isEmptyString(row.get("equipment_type_id"))) {
                await Model.searchSingleRecord("lme/dispatch/match-trailer-type", {id: row.get("equipment_type_id")},
                    "lme/dispatch/EquipmentTypeAppliesToTrailer").then(equipRow => {
                        row.set("pnn_trailer_length", equipRow.get("equip_length"));
                        row.set("pnn_trailer_width", equipRow.get("equip_width"));
                    })
            }

            // If pnn_trailer_length is not set above and is currently null, set it to 53
            if (StringUtil.isEmptyString(row.get("pnn_trailer_length"))) {
                row.set("pnn_trailer_length", 53);
            }

            // If the weight is null, default it to 40,000
            if (StringUtil.isEmptyString(row.get("weight"))) {
                row.set("weight", 40000);
            }
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    validateArrivalWindow(): ValidationResult | null {
        const row = this.mainDataSource.activeRow;
        if (!row) return null;

        // Only validate when the late field is visible (hidden when schedArriveLate is empty)
        if (!this.textboxOverrideArriveLate.visible) return null;

        const earlyValue = row.get("override_arrive_early");
        const lateValue = row.get("override_arrive_late");

        if (!earlyValue || !lateValue) return null;

        const earlyDate = new Date(earlyValue);
        const lateDate = new Date(lateValue);

        if (lateDate <= earlyDate) {
            return {
                isValid: false,
                component: this.textboxOverrideArriveLate,
                caption: "Override Latest Arrival",
                validationMessage: "Override Latest Arrival must be after Override Pickup Time."
            };
        }
        return null;
    }

    setLoadboardFieldVisibility(visible: boolean) {
        if (PermissionsUtil.isUserDeniedLayout("lme/dispatch/PostToLoadBoards")) {
            this.switchPostToLoadBoards.visible = false;
            visible = false; // If the user is denied, hide all load board fields
        }else if(!Orders.pwbLoadBoardServiceExist){
            this.switchPostToLoadBoards.visible = true;
            this.switchPostToLoadBoards.enabled = false;
            this.switchPostToLoadBoards.disabledTooltip = "No active load board services.";
            visible = false; // If no load board services are active, hide all load board fields
        }
        const orderRow = this.mainDataSource?.activeRow;
        const overrideArriveLate = orderRow?.get("override_arrive_late");
        this.textboxPnnCallback.visible = visible;
        this.checkboxTarps.visible = visible;
        this.checkboxExpedited.visible = visible;
        this.textboxPnnLoadboardUserId.visible = visible;
        this.textboxPnnTrailerLength.visible = visible;
        this.textboxPnnTrailerWidth.visible = visible;
        this.textboxPnnRateType.visible = visible;
        this.textboxPnnRate.visible = visible;
        this.textboxPnnComment.visible = visible;
        this.textboxPnnComment2.visible = visible;
        this.postingCityState.visible = visible;
        this.textboxOverrideArriveEarly.visible = visible;
        this.textboxOverrideArriveLate.visible = (overrideArriveLate !== undefined && visible);
        this.andLabel.visible = (overrideArriveLate !== undefined && visible);
    }


    checkboxPreloadedOnChange(event: ChangeEvent) {
        const checkBoxPreloaded = event.target as Checkbox;
        this.textboxPreloadTrailerId.enabled = checkBoxPreloaded.checked;
        if (!this.textboxPreloadTrailerId.enabled) {
            this.textboxPreloadTrailerId.text = null;
        }
    }

    setupPreloadedTrailerLookup() {
        const data = this.sourceStop?.data[0]?.get("location");
        if (data != null && data.length > 0) {
            const isTrailerPool = data[0].is_trailer_pool;
            const usePool = isTrailerPool == "Y";
            this.setPreloadedTrailerLookupInfo(usePool);
        }
    }

    setPreloadedTrailerLookupInfo(usePool: boolean) {
        if (usePool) {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/trailer-pool";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerPoolLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelResultField = "trailer_id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = true;
            this.textboxPreloadTrailerId.placeholder = "Trailer Pool";
        } else {
            this.textboxPreloadTrailerId.lookupModel = "lme/dispatch/asset/trailer-lookup";
            this.textboxPreloadTrailerId.lookupModelLayout = "lme/dispatch/asset/TrailerLookup"
            this.textboxPreloadTrailerId.lookupModelDisplayField = "id";
            this.textboxPreloadTrailerId.lookupModelResultField = "id";
            this.textboxPreloadTrailerId.lookupModelAllowFreeform = false;
            this.textboxPreloadTrailerId.placeholder = "All Trailers";
        }
    }

    createPostToLoadBoardDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Orders on hold may not be posted to the load boards.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const lblCaptionLine2 = new Label({
            text: "Remove the hold to post this order.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        })
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        })
        pnlCaption.add(lblCaptionLine1, lblCaptionLine2);

        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();
    }

    async shouldAskToResend() {
        const orderChangedData = this.activeRow.getChangedData();
        const overrideType = this.mainDataSource.activeRow.get("movement.override_type");
        const hasEdiCarrier = this.mainDataSource.activeRow.get("has_edi_carrier");
        if (overrideType == "E" && orderChangedData.hasOwnProperty("pieces") &&
            this.mainDataSource.activeRow.isNull("movement.xfer2settle_date") && !hasEdiCarrier) {
            const message = "Do you want to resend rate confirmation?";
            return await CommonDialogs.showYesNo(message, "Pieces Updated");
        }
        else if ((overrideType == "C" || overrideType == "T") && orderChangedData.hasOwnProperty("weight") &&
            this.mainDataSource.activeRow.isNull("movement.xfer2settle_date") && !hasEdiCarrier) {
            const message = "Do you want to resend rate confirmation?";
            return await CommonDialogs.showYesNo(message, "Weight updated");
        }
        return false;
    }

    createUpdateDeniedDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Order has been billed and you do not have permission to update an order " +
                "after the order has been billed",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const pnlCaption = new Panel({
            marginRight: 8,
            verticalAlign: VerticalAlignment.TOP,
            fillHeight: true,
            rowBreak: false
        });
        pnlCaption.add(lblCaptionLine1);
        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.dsp.EntryOrder", this.mainDataSource.activeRow.get("id"));
    }

    /** This is an event handler for the onClick event of buttonReleaseToBilling.  */
    buttonReleaseToBillingOnClick(event: ClickEvent) {
        const order_id = this.activeRow?.get("id");
        if (order_id) {
            Api.post("lme/powerbroker/release-to-billing", { order_id }).then(output => {
                const success = output.data[0].success;
                const error_message = output.data[0].error_message;
                if (success) {
                    Toast.showSuccessToast("Released to Invoicing", "Order " + order_id);
                    this.activeRow.set("ready_to_bill", "Y");
                    const setBolReceived = getDispatchControlBoolean("set_bol_received", false);
                    if (setBolReceived)
                        this.activeRow.set("bol_received", "Y");

                    this.activeRow.setValueAndLookupModelData("billing_user_id",
                        LMEUserSettings.getUserId(),
                        LMEUserSettings.getUserLookupModelData());

                    this.activeRow.resetOriginalData("ready_to_bill");
                    this.activeRow.resetOriginalData("bol_received");
                    this.activeRow.resetOriginalData("billing_user_id");
                    this.saveButton["_setEnabledFromDataSources"]();
                } else {
                    Toast.showToast(error_message);
                }
            });
        }
    }

    private checkReleaseToBillingAndDR() {
        const status = this.activeRow.get("status");
        let disabledReleaseTooltipMessage = "";
        const disabledDRTooltipMessage = "Delivery Receipt is not available for spot orders."
        if (isSpotOrder(this.activeRow)) {
            disabledReleaseTooltipMessage = "Release to invoicing is not available for spot orders.";
            this.buttonDeliveryReceipt.enabled = false;
            this.buttonDeliveryReceipt.disabledTooltip = disabledDRTooltipMessage;
            this.buttonDeliveryReceipt.tooltip = null;
        } else if (status != "D" && status != "P") {
            disabledReleaseTooltipMessage = "Cannot release to invoicing: Order status must be in progress or delivered.";
        } else if (!this.activeRow.isNull("xferred2billing_dt") || !this.activeRow.isNull("bill_date")) {
            disabledReleaseTooltipMessage = "Order has already been transferred to invoicing.";
        }
        if (disabledReleaseTooltipMessage.length > 0) {
            this.buttonReleaseToBilling.enabled = false;
            this.buttonReleaseToBilling.disabledTooltip = disabledReleaseTooltipMessage;
            this.buttonReleaseToBilling.tooltip = null;
        }
        else {
            this.buttonReleaseToBilling.enabled = true
            this.buttonReleaseToBilling.tooltip = "Release to invoicing";
        }
    }

    private _setEquipTypeOptions() {
        if (this.mainDataSource.activeRow.get("show_equip_type_options", null)) {
            this.checkboxExpedited.visible = true;
            this.checkboxTarps.visible = true;

            const options = this.mainDataSource.activeRow?.get("equipment_type_options");
            if (options != null) {
                const splitOptions = options.split("|");
                for (let i = 0; i < splitOptions.length; i++) {
                    if (equipmentTypeOptionsMap.expedited === splitOptions[i]) {
                        this.checkboxExpedited.checked = true;
                    } else if (equipmentTypeOptionsMap.tarps === splitOptions[i]) {
                        this.checkboxTarps.checked = true;
                    }
                }
            }
        } else {
            this.checkboxExpedited.visible = false;
            this.checkboxTarps.visible = false;
        }
    }

    /** This is an event handler for the onClick event of buttonSendBOL.  */
    async buttonSendBOLOnClick(event: ClickEvent) {
        const shipper = this.sourceStop.data[0];
        BillOfLading.showBillOfLadingSlideout({
            order_id: this.activeRow.get("id"),
            customer_id: this.activeRow.get("customer_id"),
            _lookup_customer_id: this.activeRow.getFirstLookupModelData("customer_id"),
            shipperLocationId: shipper.get("location_id", null),
            bol_note: this.activeRow.get("bol_note", ""),
            onError: (message: string) => Snackbar.showWarningSnackbar(message, { warningTitle: "BOL Generation Error" })
        });
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    private executeDoAfterAction() {
        if (this.doAfterAction != null)
            this.doAfterAction();
    }

    createRecurringOrder(event: ClickEvent) {
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId == null) return;

        function makeApiCall(userEnteredRecurringOrderId?: String) {
            Api.post("lme/dispatch/create-recurring-order", {
                order_id: orderId,
                recurring_order_id: userEnteredRecurringOrderId
            }).then(output => {
                const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
                const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };
                const recurringOrderId = output.data[0].recurring_order_id;
                const url = "lme/dispatch/RecurringOrders?mode=update&key="+ recurringOrderId;
                const toastMessage = "Recurring Order";
                const toastPanel = new Panel({
                    align: HorizontalAlignment.CENTER, fillRow: true,
                    components: [
                        new Label({ ...labelProps, text: toastMessage }),
                        new Label({ ...labelLinkProps, text: recurringOrderId, onClick: event => Navigation.navigateTo( url, { newTab: true })}),
                        new Label({ ...labelProps, text: " has been created."})
                    ]
                });

                Toast.showToast(toastPanel, null, { millisUntilDismissal: 7000 });
            }).catch(error => {
                for (const message of error.messages) {
                    if (message.includes("already exists in the table")) {
                        Snackbar.showWarningSnackbar("Duplicate recurring order ID");
                        return;
                    }
                }
                Snackbar.showWarningSnackbar(error.messages[0]);
            });
        }

        if (getDispatchControlBoolean("recurring_override", false)) {
            const dialogLayout = Layout.getLayout("lme/dispatch/RecurringOrderPrompt") as RecurringOrderPrompt;
            CommonDialogs.showYesNo(dialogLayout, "Create Recurring Order", {
                yesButtonCaption: "OK",
                noButtonCaption: "Cancel"
            }, false, 150).then(clickedYes => {
                if (clickedYes) {
                    const userEnteredRecurringOrderId: String = dialogLayout.textboxRecurringOrderId.text;
                    makeApiCall(userEnteredRecurringOrderId);
                }
            });
        } else {
            makeApiCall();
        }
    }

    convertSpotToOrder(event: ClickEvent) {
        if ("C" == this.mainDataSource.activeRow.get("subject_order_status")) {
            Snackbar.showWarningSnackbar("This spot order has already been converted into an order.", { targetPanel: this });
            return;
        }
        if (this.activeRow.get("customer_id") == null) {
            this.textboxCustomerId.required = true;
            this.textboxCustomerId.validateSimple();
            this.textboxCustomerId.required = false;
            Snackbar.showWarningSnackbar("A customer is required to convert this spot order to an order", { targetPanel: this });
            return;
        }

        Api.post("lme/dispatch/convert-spot-to-order", { spot_id: this.activeRow.get("id") }).then(output => {
            const errorReason = output.data[0].error_reason;
            if (errorReason != null) {
                Snackbar.showWarningSnackbar(errorReason, { targetPanel: this });
            } else {
                const orderId = output.data[0].order_id;
                this.mainDataSource.search({ id: this.activeRow.get("id") }).then(() => {
                    const toast = Toast.showToast(this.getOrderCreatedToast(orderId, "created", () => this.orderToastLinkOnClick(orderId, toast)), null);
                });
            }
        });
    }

    voidOrder(event: ClickEvent) {
        if ("V" == this.mainDataSource.activeRow.get("status")) {
            Snackbar.showWarningSnackbar("This order is already void.", { targetPanel: this });
            return;
        }
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId != null) {
            VoidOrderPrompt.showPrompt(orderId, (success: boolean, promptContent: VoidOrderPrompt) => {
                if (success) {
                    this.mainDataSource.search({ id: orderId }).then(() => {
                        if (this.parent instanceof OrderTabView) {
                            const layoutMovementTabView = this.parent.layoutMovementTabView as MovementTabView;
                            if (layoutMovementTabView.activeRow) {
                                layoutMovementTabView.layoutMovementTabs.currentTabId = null;
                                layoutMovementTabView.search({ id: layoutMovementTabView.activeRow.get("id") });
                            }
                        }

                        const toast = Toast.showToast(this.getOrderCreatedToast(orderId, "voided", () => this.orderToastLinkOnClick(orderId, toast)), null);
                        this.dataHeader?.syncTitle();
                    });
                } else {
                    const snackMessage = `Could not void order ${orderId}`;
                    Snackbar.showWarningSnackbar(snackMessage);
                }
            });
        }
    }

    createRate(event: ClickEvent): void {
        if (this.mainDataSource.hasChanged(true)) {
            Snackbar.showWarningSnackbar("Order has unsaved changes. Please save before creating rate.", { warningTitle: "Cannot Create Rate" });
            return;
        }

        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId != null) {
            CommonDialogs.showYesNo("Create New Rate Record Now?", "Create Rate", {
                yesButtonProps: {
                    onClick: async () => {
                        const progressSnackbar = Snackbar.showDownloadSnackbar(
                            "Creating Rate", "Creating rate record for order " + orderId,
                            { targetPanel: MainPageAccessor.router, persist: true }
                        );
                        return Api.post("lme/dispatch/order-to-rate", { order_id: orderId })
                            .then(output => {
                                const rateId = output.data?.[0]?.rate_id;
                                const autoRatePanel = new CrudDecorator({
                                    layout: Layout.getLayout("lme/dispatch/rates/AutoRate"),
                                    mode: DataSourceMode.UPDATE,
                                    key: rateId,
                                    headerProps: {
                                        showClose: true,
                                        showSaveAndClose: true,
                                        showDelete: true
                                    }
                                });
                                return autoRatePanel.slideIn({ speed: 200 });
                            }).finally(() => progressSnackbar.dismiss());
                    }
                }
            });
        }
    }

    private async _copyOrder(event: ClickEvent) {
        const orderId = this.mainDataSource.activeRow?.get("id");

        await Model.search("lme/dispatch/copy-control-lookup").then((response) => {
            if (response.actualRowCount > 1) {
                const copyDialogLayout = Layout.getLayout("lme/dispatch/CopyOrderPrompt") as AutogenLayoutCopyOrderPrompt;
                CommonDialogs.showYesNo(copyDialogLayout, "Copy Order", {
                    yesButtonCaption: "Create Order",
                    noButtonCaption: "Cancel"
                }).then(clickedYes => {
                    if (clickedYes) {
                        const companyId = copyDialogLayout.textboxCompanyId.lookupModelData?.[0]?.get("id");
                        this._postCopyOrder(orderId, companyId);
                    }
                });
            } else {
                const copyOrderLabel = new Label({text: "Are you sure you want to copy this order?", align: HorizontalAlignment.CENTER, fontSize: "large"});
                CommonDialogs.showYesNo(copyOrderLabel, "Copy Order", {
                    yesButtonCaption: "Create Order",
                    noButtonCaption: "Cancel"
                }).then(clickedYes => {
                    if (clickedYes) {
                        const companyId = response.modelRows[0].get("copy_to_company");
                        this._postCopyOrder(orderId, companyId);
                    }
                });
            }
        });
    }

    private async _postCopyOrder(orderId: string, companyId: string) {
        const creatingOrderSnackbar = Snackbar.showDownloadSnackbar("Copy Order", "Creating order.", {persist: true});
        return Api.post("lme/dispatch/copy-order", {
            order_id: orderId,
            company_id: companyId
        }, null, null).then(response => {
            creatingOrderSnackbar.dismiss();
            const data = response.data[0];
            const orderIds = data["duplicate_orders"];
            const hasFailedOrders = data["has_failed_orders"];
            const failedCredit = data["failed_credit"];
            if (hasFailedOrders) {
                const snackbarMessage: string = "Unable to create order. " +
                    (failedCredit ? companyId + " has exceeded their credit limit. " : "") +
                    (data.failure_message ? "\n\nReason: " + data.failure_message : "");

                this.creditValidator.showCustomerCreditValidationSnackbar(snackbarMessage);
            }
            if (orderIds.length > 0) {
                const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
                const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };
                let url = "";
                const mode = "?mode=update&key=";
                const sharedCompany = companyId.toLowerCase();
                const currentCompany = UserSettings.get()?.company_id.toLowerCase();
                const currentCompanyLength = currentCompany?.length;
                const origin = window.location.origin;
                const originIndex = origin.indexOf(currentCompany+"-");
                const pathname = "/lme/dispatch/OrderTabView";
                if (originIndex < 0) {
                    const host = StringUtil.stringAfter(window.location.host, "-");
                    const protocol = window.location.protocol;
                    url = `${protocol}//${sharedCompany}-${host}${pathname}${mode}${orderIds[0]}`;
                } else {
                    const substring1 = origin.substring(0, originIndex);
                    const substring2 = origin.substring(originIndex + currentCompanyLength + 1);
                    url = `${substring1}${sharedCompany}-${substring2}${pathname}${mode}${orderIds[0]}`;
                }
                const toastPanel = new Panel({
                    align: HorizontalAlignment.CENTER, fillRow: true,
                    components: [
                        new Label({ ...labelProps, text: "Created order" }),
                        new Label({ ...labelLinkProps, text: orderIds[0], onClick: event => window.open(url, "_blank")}),
                        new Label({ ...labelProps, text: " for company " + companyId + "."})
                    ]
                });
                Toast.showToast(toastPanel, null, { millisUntilDismissal: 7000 });
                this.textboxCopyToCompany.text = companyId;
                this.textboxCopyToOrderId.text = orderIds[0];
            }
        });
    }

    private async _duplicateOrder() {
        const orderId: string = this.mainDataSource.activeRow.get("id");

        const response = await Api.post("lme/dispatch/duplicate-order-validation", { order_id: orderId });
        const warnings: string[] = response?.data[0].warnings;
        warnings?.forEach(warning => {
            Snackbar.showSnackbar(warning);
        })
        if (warnings?.length > 0) return;

        const dialogLayout = Layout.getLayout("lme/dispatch/DuplicateOrderPrompt") as DuplicateOrderPrompt;
        dialogLayout.addLayoutLoadListener(() => {
            dialogLayout.numbereditorOrdersCreate.value = 1;
            if (this.isBrokerageLtlCompany) {
                dialogLayout.panelBrLTLOptions.visible = true;
                if (this.activeRow.getBoolean("is_brokerage_ltl", false)) {
                    dialogLayout.panelOrderOptions.visible = false;
                } else {
                    dialogLayout.panelOrderOptions.align = HorizontalAlignment.LEFT;
                    dialogLayout.panelBrLTLOptions.align = HorizontalAlignment.LEFT;
                    dialogLayout.panelOrderOptions.paddingLeft = dialogLayout.panelBrLTLOptions.paddingLeft = 70;
                    dialogLayout.checkboxPerItem.visible = false;
                }
            }
            if (getExciseTaxControlBoolean("is_active", false)) {
                dialogLayout.panelExciseTaxOptions.visible = true;
            }
            dialogLayout.sourceCopyControl.search({ "copy_type": "D" });
        });
        CommonDialogs.showYesNo(dialogLayout, "Duplicate Orders", {
            yesButtonCaption: "Create Orders",
            noButtonCaption: "Cancel"
        }, false, 150).then(clickedYes => {
            if (clickedYes) {
                const howManyOrders = dialogLayout.numbereditorOrdersCreate.value;

                // Values are opposite to checked values because copy control tell us what to exclude not include so we need to look at the checked property itself
                const copyRate: boolean = dialogLayout.checkboxIncludeRate.checked && !this.activeRow.get("is_brokerage_ltl");
                const copyOtherCharges: boolean = dialogLayout.checkboxIncludeOtherCharges.checked && !this.activeRow.get("is_brokerage_ltl");
                const includePerItem: boolean = dialogLayout.checkboxPerItem.checked;
                const includeHandlingRequirements: boolean = dialogLayout.checkboxHandlingReq.checked;
                const includeExciseTaxes: boolean = dialogLayout.checkboxExciseTaxes.checked;

                this.checkDuplicateBOL.validate(DuplicateBOLSource.DUPLICATE, this.saveButton)
                    .then(result => {
                        if (!result.success) {
                            CommonDialogs.showMessage(result.reason, "", this.checkDuplicateBOL.getDialogProps(true, "Duplicate BOL"))
                        } else {
                            const progressSnack = Snackbar.showDownloadSnackbar("Duplicate Order", "Creating " + howManyOrders + " order(s).");
                            return Api.post("lme/dispatch/duplicate-order", {
                                order_id: orderId,
                                how_many: howManyOrders,
                                fail_on_credit_warning: true,
                                copy_rate: copyRate,
                                copy_other_charges: copyOtherCharges,
                                include_per_item: includePerItem,
                                include_handling_requirements: includeHandlingRequirements,
                                include_excise_taxes: includeExciseTaxes
                            }, null, null).then(response => {
                                const data = response.data[0];
                                const orderIds = data["duplicate_orders"];
                                const hasFailedOrders = data["has_failed_orders"];
                                const failedCredit = data["failed_credit"];
                                if (hasFailedOrders) {
                                    progressSnack.dismiss();
                                    const snackbarMessage: string = "Unable to create all orders. " +
                                        (failedCredit ? this.textboxCustomerId.text + " has exceeded their credit limit. " : "") +
                                        (orderIds.length) + " order(s) of " + howManyOrders + " were created." +
                                        (response?.data[0].failure_message ? "\n\nReason: " + response?.data[0].failure_message : "");

                                    this.creditValidator.showCustomerCreditValidationSnackbar(snackbarMessage);
                                }
                                if (orderIds.length > 0)
                                    Navigation.navigateTo("lme/dispatch/OrderList?ids=" + orderIds.toString(), { newTab: false });
                            });
                        }
                    });
            }
        });
    }

    private _unvoidOrder(event: ClickEvent) {
        if ("V" != this.mainDataSource.activeRow.get("status")) {
            Snackbar.showWarningSnackbar("This order is not voided.", { targetPanel: this });
            return;
        }
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId != null) {
            UnvoidOrderPrompt.showUnvoidPrompt(orderId, (success: boolean, promptContent: UnvoidOrderPrompt) => {
                if (success) {
                    this.mainDataSource.search({ id: orderId }).then(() => {
                        if (this.parent instanceof OrderTabView) {
                            const layoutMovementTabView = this.parent.layoutMovementTabView as MovementTabView;
                            if (layoutMovementTabView.activeRow) {
                                layoutMovementTabView.layoutMovementTabs.currentTabId = null;
                                layoutMovementTabView.search({ id: layoutMovementTabView.activeRow.get("id") });
                            }
                        }

                        const toast = Toast.showToast(this.getOrderCreatedToast(orderId, "unvoided", () => this.orderToastLinkOnClick(orderId, toast)), null);
                        this.dataHeader?.syncTitle();
                    });
                } else {
                    const snackMessage = `Could not unvoid order ${orderId}`;
                    Snackbar.showWarningSnackbar(snackMessage);
                }
            });
        }
    }

    buttonOrderHistoryOnClick(event: ClickEvent) {
        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: this.activeRow.get("id"),
            movementId: this.activeRow.get("curr_movement_id"),
            isBilled: !this.activeRow.isNull("bill_date") || !this.activeRow.isNull("xferred2billing_dt"),
        }
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
    }

    buttonOrderHistoryWithAlertsOnClick(event: ClickEvent) {
        this.buttonOrderHistoryOnClick(event);
    }


    equipItemTableOnRowDisplay(event) {
        const row: TableRow = event.getTableRow();
        let equipType = (row.findComponentById("textboxMatchEquipmentTypeId")) as Textbox;
        if (equipType == null) {
            equipType = (row.findComponentById("textboxMatchEquipmentTypeIdAsset")) as Textbox;
        }
        if (equipType != null && row.data.get("equipment_match_id") != undefined) {
            //card is in CWG backlog to be able to remove this tooltip hack
            equipType["_internalShowTooltip"] = (tooltip: ComponentCreator, originatingEvent: MouseEvent) => {
                const panel = new Panel({ padding: 0, margin: 0 });
                panel.add(new Label({ text: "Match ID", fontSize: "medium", fontBold: true }));
                panel.add(new Label({ text: row.data.get("equipment_match_id") }));
                return panel;
            }
        }

        if (this.tableEquipmentRequirementsAsset.visible && this.tableEquipmentRequirementsAsset.quickAddRow === row) {
            row.data.set("is_shipper_consignee", "B");
            const textboxShipCons = row.findComponentById("textboxShipperConsignee") as Textbox;
            if (textboxShipCons != null) {
                textboxShipCons.enabled = false;
            }
        }
    }

    textboxMatchEquipmentTypeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.hasSelection() === true) {
            const appliesTo: string = event.selection.get("applies_to") as string;
            const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
            const textboxAppliesTo = tableRow.findComponentById("textboxAppliesTo") as Textbox;
            if (textboxAppliesTo) {
                textboxAppliesTo.setSelectedItemFromValue(appliesTo);
                event.target.boundRow.set("applies_to", appliesTo);
            }
        }
    }

    textboxLoadReqAppliesToOnChange(event: ChangeEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
        this.updateShipperConsignee(tableRow, event.newValue);
    }

    updateShipperConsignee(tableRow: TableRow, newValue: string) {
        const shipperConsignee = tableRow.findComponentById("textboxShipperConsignee") as Textbox;
        if (shipperConsignee == null) {
            return;
        }

        if (StringUtil.isEmptyString(newValue) || ["Tractor", "Trailer"].includes(newValue)) {
            shipperConsignee.enabled = false;
            shipperConsignee.setSelectedItemFromValue("B");
        } else {
            shipperConsignee.enabled = true;
        }
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }

    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }

    public setEdiFields() {
        const moveType = this.mainDataSource.activeRow.get("def_move_type");
        this.buttonFindCarrier.visible = moveType == "B";
        this.buttonAssignCarrier.visible = moveType == "B" && !this.labelCreditBadge.visible;
        this.buttonViewOrder.visible = moveType == "A" || moveType == "B";
        this.buttonAssignDriver.visible = moveType == "A";
        this.switchPostToLoadBoardsNextAction.visible = moveType == "B";
        this.buttonCarrierRates.visible = false;
    }

    async stopsTableOnContentsChanged(event: TableContentsChangedEvent) {
        if (TableAction.DELETE == event.getAction()) {
            this.checkStopsForDistanceChange().then(() => {
                this.layoutRates.calculateRates(false, false);
            });
        }
    }

    private validateRespFilteringDuringAdd(event: DataSourceValidationEvent, buttonUsers: Button) {
        if (event.dataSource.activeRow.get("rf_entry_code_required", false) !== true)
            return;
        let rfDataSource: DataSource = null;
        if (event.childDataSources != null) {
            for (const childDataSource of event.childDataSources) {
                if (childDataSource.url === "lme/general/responsible-hist") {
                    rfDataSource = childDataSource;
                    break;
                }
            }
        }
        if (rfDataSource != null) {
            for (const row of rfDataSource.data) {
                if (row.get("responsible_role", null) === "E" &&
                    (row.get("hierarchy_level_1", null) != null || row.get("hierarchy_level_2", null) != null || row.get("hierarchy_level_3", null) != null ||
                        row.get("hierarchy_level_4", null) != null || row.get("hierarchy_level_5", null) != null)) {
                    //an entry row is present, return without adding anything to the validation results
                    return;
                }
            }
        }
        const message = "A responsibility record with role 'Entry' must be specified";
        event.validationResults.push({
            isValid: false,
            validationMessage: message,
            caption: message,
            component: buttonUsers
        });
    }

    private get saveButton(): SaveButton {
        return this.dataHeader?.["saveButton"];
    }

    public displayOrderCreatedToast(orderId?: string) {
        const id = orderId ?? this.mainDataSource?.activeRow?.get("id", null);
        const toast = Toast.showToast(this.getOrderCreatedToast(id, "created", () => this.orderToastLinkOnClick(id, toast)), null);
    }

    private orderToastLinkOnClick(key: string, toast: Toast) {
        OrderTabView.navigateTo({ key, newTab: false });
        toast.dismiss();
    }

    private getOrderCreatedToast(orderId: string, action: string, doOnLinkClick: () => void): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0
        };
        let labels: Label[] = [];
        if (!StringUtil.isEmptyString(orderId)) {
            labels = [
                new Label({ ...labelProps, text: "Order " }),
                new Label({
                    ...labelProps,
                    style: { textDecoration: "underline" },
                    text: orderId,
                    onClick: event => doOnLinkClick()
                }),
                new Label({ ...labelProps, text: " " + action + "." })
            ];
        } else {
            labels = [
                new Label({ ...labelProps, text: "Order not " + action + "." })
            ];
        }
        return new Panel({ align: HorizontalAlignment.CENTER, fillRow: true, components: labels });
    }

    imageAddHandlingReqOnClick() {
        OrderHandlingRequirementsSlideout.showSlideout({
            hdrDataSource: this.sourceBrltlOrderHdrXFgp,
            hdrChanged: (row: ModelRow, type: ModelDataChangeType) => this.hdrChanged(row, type),
            onSave: (hasChanged: boolean) => {
                this.layoutHandlingReqs.populateHdrs(this.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                    this.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                });
            },
            invokedFromEdi: false
        })
    }

    buttonDeliveryReceiptOnClick() {
        DeliveryReceipt.showDeliveryReceiptSlideout({
            order_id: this.activeRow.get("id"),
            customer_id: this.activeRow.get("customer_id"),
            _lookup_customer_id: this.activeRow.getFirstLookupModelData("customer_id")
        });
    }

    hdrChanged(hdr: ModelRow, type: ModelDataChangeType) {
        if (type == ModelDataChangeType.ADD) {
            hdr.set("fgp_uid", this.mainDataSource.activeRow.get("fgp_uid"));
            hdr.set("order_id", this.mainDataSource.activeRow.get("id"));
            hdr._appending = true;
            this.sourceBrltlOrderHdrXFgp.addRow(hdr, this.sourceBrltlOrderHdrXFgp.data.length, false);
        } else if (type == ModelDataChangeType.DELETE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach(row => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    this.sourceBrltlOrderHdrXFgp._deleteTableRow(row, true);
                }
            });
        } else if (type == ModelDataChangeType.UPDATE) {
            this.sourceBrltlOrderHdrXFgp.data.forEach((row, index) => {
                if (row?.data["hdr_uid"] == hdr.data["hdr_uid"]) {
                    row.setValues({
                        "text_value": hdr.data["text_value"],
                        "integer_value": hdr.data["integer_value"],
                        "float_value": hdr.data["float_value"],
                        "time_value": hdr.data["time_value"],
                        "date_value": hdr.data["date_value"]
                    });
                }
            });
        }

    }

    enableStatusButton() {
        const orderStatus = this.mainDataSource.activeRow?.get("status");
        this.buttonInitiateStatusProcessing.enabled = (orderStatus != "Q" && orderStatus != "V" && orderStatus != "S");
    }

    async initiateStatusProcessing(event: ClickEvent): Promise<any> {
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId == null) return;
        this.buttonInitiateStatusProcessing.busy = true;
        const mode = this.mainDataSource.mode;
        this.mainDataSource.activeRow?.set("shipstatus2edi_dt", null);
        this.mainDataSource.displayDataInBoundComponent(this.textboxOutboundStatusCompleted);

        await this.validateProcessStatus(orderId, mode).then(async response => {
            await this.delay(3000).then(() => {
                this.sourceOrders.search({ id: orderId }).then((order) => {
                    const row = order.modelRows[0].data;
                    const statusCompletedDate = row["shipstatus2edi_dt"];
                    this.textboxOutboundStatusCompleted.text = statusCompletedDate;
                    this.mainDataSource.displayDataInBoundComponent(this.textboxOutboundStatusCompleted);
                });
            });
        });
        this.buttonInitiateStatusProcessing.busy = false;
    }

    async validateProcessStatus(orderId: string, mode: string): Promise<any> {
        return await Api.post("lme/datafusion/status-processing", { order_id: orderId, datasource_mode: mode });
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    sourceBrltlBillingFreightGroupAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            if (this["selectCarrier"] === true) {
                this.showCarrierSelectionSlideout();
                this["selectCarrier"] = false;
            }
        }
    }

    sourceBrltlOrderHdrXFgpAfterExecution(event: DataSourceExecutionEvent) {
        this.layoutHandlingReqs.populateHdrs(event.dataSource.data, true);
    }

    getFreightGroupItemDataSource(): DataSource {
        return this.sourceBrltlOrderFreightGroupItem;
    }

    getStopDataSource(): DataSource {
        return this.sourceStop;
    }

    getHdrXFgpDataSource(): DataSource {
        return this.sourceBrltlOrderHdrXFgp;
    }

    getBillingFreightGroupDataSource(): DataSource {
        return this.sourceBrltlBillingFreightGroup;
    }


    switchPostToAllOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.userInitiatedChange) {
            if (event.newValue) {
                this.textboxPreferredVendors.visible = false;
                this.textboxPreferredVendors.visibleDuringUpdate = false;
                this.textboxPreferredVendors.required = false;
            } else {
                this.textboxPreferredVendors.visible = true;
                this.textboxPreferredVendors.visibleDuringUpdate = true;
                this.textboxPreferredVendors.required = true;
            }
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxOrderType. */
    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    async cancelLoadBoardPosting(orderId: string, movementId: string) {
        if (StringUtil.isEmptyString(orderId) || StringUtil.isEmptyString(movementId)) {
            return;
        }
        Api.post("lme/powerbroker/cancel-loadboard-posting",
            { order_id: orderId, movement_id: movementId })
            .then(response => {
                if (response?.data[0]?.is_cancel_successful) {
                    this.updateLoadboardFlag(orderId, true);
                    this.displaySuccessfulCancelToastMessage(orderId, movementId);
                } else {
                    this.updateLoadboardFlag(orderId, false);
                    this.displayFailedCancelToastMessage(orderId, response?.data[0]?.successful_vendor_cancellations, response?.data[0]?.failed_vendors);
                }
            })
    }

    private async updateLoadboardFlag(orderId: string, isSuccess: boolean) {
        const rowOrder = await new ModelOrders().searchSingle({ id: orderId });
        rowOrder.set("loadboard_posting_cancelled", isSuccess);
        await rowOrder.post();
    }


    displaySuccessfulCancelToastMessage(orderId: string, movementId: string) {
        const lbl = new Label({ text: `Success!\n\rThe load posting for Order ${orderId} has been canceled`, align: HorizontalAlignment.CENTER, fontBold: true, fontSize: "xxlarge" });
        Toast.showToast(lbl, { backgroundColor: "primary" });
    }

    displayFailedCancelToastMessage(orderId: string, successfulCancellationsVendors: string, failedCancellationsVendors: string) {
        if (!StringUtil.isEmptyString(successfulCancellationsVendors) && !StringUtil.isEmptyString(failedCancellationsVendors)) {
            const partialSuccessful = `Success! The load posting for ${orderId} has been canceled for: ${successfulCancellationsVendors}`;
            Toast.showToast(partialSuccessful, null, { persist: true });

            const partialFailure = `The load posting for Order ${orderId} was not canceled for: ${failedCancellationsVendors}. Please retry directly on the load board site.`
            Snackbar.showWarningSnackbar(partialFailure);
        }
        else if (!StringUtil.isEmptyString(successfulCancellationsVendors)) {
            const loadBoardCancellationSuccessful = `${successfulCancellationsVendors} cancellation of load posting was successful.`;
            Toast.showToast(loadBoardCancellationSuccessful, null, { persist: true });
        } else if (!StringUtil.isEmptyString(failedCancellationsVendors)) {
            const loadBoardCancellationFailed = `${failedCancellationsVendors} cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        } else {
            // This condition occurs when there is not a matching load_board_trx record for the order_id and movement_id.
            const loadBoardCancellationFailed = `Cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        }
    }


    public set currencyFromProfile(value: string) {
        this._currencyFromProfile = value;
    }

    private getConversionDateFromStops(): Date {
        const conversionBy: string = CurrencySettings.getConversionBy();
        const numberOfStops = this.stopsTable.rows.length;
        if (numberOfStops > 0) {
            const stop: TableRow = "S" == conversionBy ? this.stopsTable.rows[0] : this.stopsTable.rows[numberOfStops - 1];
            return stop.data.get("actual_arrival") ?? stop.data.get("sched_arrive_early") ?? new Date();
        }
        return new Date();
    }

    public getCurrencyFromProfile(): string {
        return this._currencyFromProfile;

    }

    async getTransactionalCurrency(): Promise<string> {
        let transactionCurrency = CurrencySettings.getTransactionalCurrency();
        const partner = this.mainDataSource.activeRow?.getString("partner_id");
        const version = this.mainDataSource.activeRow?.getString("partner_version");
        if (this.getInvokedFromEdi() && this.getCurrencyFromProfile()) {
            transactionCurrency = this.getCurrencyFromProfile();
        } else if (partner && version) {
            const profile: RowEdiorderProfile = await new ModelEdiorderProfile().searchSingle({
                partner_id: partner,
                version: version
            });
            if (profile.data.currency_type_id) {
                transactionCurrency = profile.data.currency_type_id;
            }
        }
        return transactionCurrency;
    }

    textboxCommodityIdOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set("commodity", null);
            this.textboxCommodity.text = null;
        }
    }

    textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const descr = event.selection.get("descr");
            this.activeRow.set("commodity", descr);
            this.textboxCommodity.text = descr;
            this.commodityCodeChanged();
        }
    }

    textboxBrokerLocationIdBeforeLookupModelSearch(event) {
        event.filter.is_customs_broker = 'Y';
    }

    textboxCbpCrossingLocationIdOnLookupModelSelection() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.textboxCbpCrossingDate.clear();
        this.textboxCbpCrossingReason.clear();
    }

    textboxCbsaCrossingLocationIdOnLookupModelSelection() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.textboxCbsaCrossingDate.clear();
        this.textboxCbsaCrossingReason.clear();
    }

    /** This is an event handler for the afterDropdownSelection event of textboxDefMoveType. */
    textboxDefMoveTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) this.enableNextActions = true;
    }

    // /** This is an event handler for the onClick event of buttonShare.  */
    buttonShareOnClick(event: ClickEvent) {
        CreateOrderLink.onClick(event, this.mainDataSource.activeRow?.get("id"));
    }

    private async initializePbwLoadBoardServiceExist(): Promise<void> {
        const result = await Api.search("lme/powerbroker/pbw-loadboard-services", { is_active: "Y" });
        Orders.pwbLoadBoardServiceExist = result?.data?.length > 0;
        this.switchPostToLoadBoards.visible = Orders.pwbLoadBoardServiceExist;
    }

    override displayInViewModeAfterLoad() {
        super.displayInViewModeAfterLoad();
        this.dataHeader.displayInViewMode();
        this.panelOrderTab.enabled = true;
        this.labelOrderView.enabled = true;
        this.panelMovementTab.enabled = true;
        this.labelMovementView.enabled = true;
        this.panelInvoiceHistoryTab.enabled = true;
        this.labelInvoiceHistory.enabled = true;
    }

    // The Order Value field has a separate currency selector independent of the main Currency Type field on the rates layout.
    async textboxOrderValueCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }

        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", await this.getTransactionalCurrency()) as string;
            const orderValue = this.mainDataSource.activeRow?.getCurrency("order_value", new Currency(0));
            if (orderValue) {
                orderValue.setCurrencyCode(currencyType);
                orderValue.setConversionDate(this.getConversionDateFromStops());
                this.mainDataSource.activeRow?.updateCurrency("order_value", orderValue);
            }
        }
    }

    async textboxOrderValueOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH || !CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            return;
        }

        if (event.changedWhileFocused && !this.mainDataSource.activeRow.isNull("order_value")) {
            const orderValue = this.mainDataSource.activeRow?.getCurrency("order_value");
            const currencyType = await this.determineOrderValueCurrencyType();

            if (orderValue) {
                orderValue.setCurrencyCode(currencyType);
                orderValue.setConversionDate(this.getConversionDateFromStops());
                this.mainDataSource.activeRow?.updateCurrency("order_value", orderValue);
            }
            this.mainDataSource.activeRow?.setValueAndLookupModelData("order_value_c", currencyType, { currency_type_id: currencyType });
        }
    }

    async determineOrderValueCurrencyType() {
        if (!this.mainDataSource.activeRow.isNull("order_value_c")) {
            // If order value is populated, don't overwrite the selected currency
            return this.mainDataSource.activeRow?.getString("order_value_c");
        } else if (this.textboxCustomerId.getFirstLookupModelData() != null && !this.textboxCustomerId.getFirstLookupModelData().isNull("currency_type")) {
            // If a customer is selected, use their default currency
            return this.textboxCustomerId.getFirstLookupModelData().getString("currency_type");
        }

        // Otherwise, use the freight charge currency type or fall back to the default transactional
        return this.mainDataSource.activeRow?.getString("freight_charge_c", await this.getTransactionalCurrency());
    }
}
function getEquipmentGroupData(equipmentGroupId: any, string: any) {
    throw new Error("Function not implemented.");
}
